import React, { useEffect, useRef, useState } from 'react'
import {
  CAlert,
  CBadge,
  CButton,
  CFormTextarea,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from '@coreui/react'
import { useBostonState, emit } from '../lib/useBoston'
import { scriptsCatalog } from '../../../services/scriptActions'
import { envConfig } from '../../../services/config'
import { Storage, uuidV4 } from '../lib/store'
import {
  csReindexTags,
  csBuildSingleFile,
  csLoadRuntimeTemplates,
  csBuildDataJson,
  csBuildConfigJson,
  csBuildPublishPayload,
  csFetchTemplate,
  csPersistAndPublish,
} from '../lib/builder'
import { useUI } from '../../../shared/ui/UIProvider'
import SstInterpreterPreview from '../components/SstInterpreterPreview'
import { displayRemarkType, normalizeRemarkField } from '../lib/remarkSemantics'

/* Port fiel de viewDevReview/bindDevReview de boston_ia:
   🚨 REVISIÓN DEV — cola de scripts enviados a revisión con feedback.
   Storage en localStorage (boston_dev_queue_v1). Al subir al server, esta capa
   devQueue es el ÚNICO punto a swapear por la API/DB (igual que las keys/URLs
   y los prompts, que pasarán a DB). */

import {
  biaCreateKnowledge,
  biaDeleteSourceFile,
  biaDownloadSourceFile,
  biaListSourceFiles,
  biaPublishReleaseCustomScript,
  biaSaveDraftCustomScript,
  biaValidateCustomScriptPublish,
  devQueueList,
  devQueueDelete,
  devQueueMarkMessagesRead,
  devQueueSaveFiles,
  devQueueSendMessage,
  devQueueSetState,
} from '../lib/biaApi'

const DEV_QUEUE_KEY = 'boston_dev_queue_v1'
const DEV_REVIEW_CHANGED_EVENT = 'boston-dev-review-queue-changed'
const DEV_REVIEW_PAGE_SIZE = 5
let devQueueMemory = null
function notifyDevReviewQueueChanged() {
  try {
    window.dispatchEvent(new CustomEvent(DEV_REVIEW_CHANGED_EVENT))
  } catch (e) {}
}

function persistDevQueueSnapshot(items) {
  const source = Array.isArray(items) ? items : []
  let lastError = null
  for (const limit of [30, 15, 5, 1]) {
    try {
      const snapshot = source.slice(-limit)
      localStorage.setItem(DEV_QUEUE_KEY, JSON.stringify(snapshot))
      return { ok: true, count: snapshot.length, truncated: snapshot.length < source.length, summaryOnly: false, error: '' }
    } catch (error) {
      lastError = error
    }
  }

  // Un único SST puede superar por sí solo la cuota de localStorage. La BD sigue
  // siendo la fuente real; para recuperación local basta conservar metadatos.
  const compactItem = (item = {}) => ({
    id: item.id,
    ts: item.ts,
    title: item.title,
    feedback: item.feedback || '',
    estado: item.estado || 'en_revision',
    published: item.published || null,
    scriptUuid: item.scriptUuid || item.script_uuid || null,
    sourceFiles: item.sourceFiles || [],
    sourceFileIds: item.sourceFileIds || [],
    username: item.username || null,
    parentPcc: item.parentPcc || item.parent_pcc || null,
    pcc: item.pcc || null,
    agency: item.agency || null,
    origin: item.origin || item.source || 'admin_v2',
    source: item.source || item.origin || 'admin_v2',
    messages: Array.isArray(item.messages) ? item.messages.slice(-20) : [],
    history: (Array.isArray(item.history) ? item.history : item.history?.events || [])
      .slice(-20)
      .map((entry) => ({ ts: entry?.ts, action: entry?.action || '', feedback: entry?.feedback || '' })),
    files: { 'feedback.txt': item.feedback || '' },
    original: '',
    _cacheSummary: true,
    _syncState: item._syncState || item._via || 'db',
  })
  for (const limit of [100, 30, 10, 1]) {
    try {
      const snapshot = source.slice(-limit).map(compactItem)
      localStorage.setItem(DEV_QUEUE_KEY, JSON.stringify(snapshot))
      return { ok: true, count: snapshot.length, truncated: snapshot.length < source.length, summaryOnly: true, error: '' }
    } catch (error) {
      lastError = error
    }
  }
  return {
    ok: false,
    count: 0,
    truncated: false,
    error: lastError?.message || 'No fue posible guardar la cola en el almacenamiento local.',
  }
}

function publishTargetEnvFromValue(value) {
  const raw = String(value || '').trim().toLowerCase()
  if (!raw) return ''
  if (raw === 'prod' || raw === 'production') return 'prod'
  if (raw === 'stg' || raw === 'stage' || raw === 'staging') return 'stg'
  if (/^https?:\/\/stg[.-]/.test(raw) || raw.includes('//stg.') || raw.includes('-stg.')) return 'stg'
  if (raw.includes('boston.basset.ws') && !raw.includes('stg.')) return 'prod'
  return ''
}

function resolveReviewPublishTargetEnv(...values) {
  for (const value of values) {
    const env = publishTargetEnvFromValue(value)
    if (env) return env
  }
  return 'stg'
}

const devQueue = {
  all() {
    if (Array.isArray(devQueueMemory)) return devQueueMemory
    try {
      return JSON.parse(localStorage.getItem(DEV_QUEUE_KEY) || '[]')
    } catch (e) {
      return []
    }
  },
  saveAll(list) {
    devQueueMemory = Array.isArray(list) ? list : []
    const persisted = persistDevQueueSnapshot(devQueueMemory)
    try {
      updateDevBadge()
      notifyDevReviewQueueChanged()
    } catch (e) {}
    return persisted
  },
  replace(list) {
    return this.saveAll(Array.isArray(list) ? list : [])
  },
  get(id) {
    return this.all().find((x) => x.id === id) || null
  },
  updateLocal(id, patch) {
    const l = this.all()
    const i = l.findIndex((x) => x.id === id)
    if (i < 0) return
    l[i] = Object.assign({}, l[i], patch)
    this.saveAll(l)
    return l[i]
  },
  update(id, patch) {
    const l = this.all()
    const i = l.findIndex((x) => x.id === id)
    if (i < 0) return
    l[i] = Object.assign({}, l[i], patch)
    this.saveAll(l)
    // PERSISTENCIA: las transiciones de estado viajan tambien a la DB (best effort)
    if (patch && patch.estado) {
      const feedback = patch.feedback !== undefined ? patch.feedback : reviewFeedback(l[i])
      try { return devQueueSetState(id, patch.estado, feedback) } catch (e) {}
    }
  },
  remove(id) {
    this.saveAll(this.all().filter((x) => x.id !== id))
  },
  pendingCount() {
    return this.all().filter((x) => (x.estado || 'en_revision') === 'en_revision').length
  },
}

// Port fiel: en la SPA original actualizaba el contador del nav (#nav-devreview-count).
// En el admin React ese nodo no existe, asi que es un no-op seguro (el if (!el) return).
function updateDevBadge() {
  const el = document.getElementById('nav-devreview-count')
  if (!el) return
  const n = devQueue.pendingCount()
  el.textContent = n > 0 ? String(n) : ''
  el.style.display = n > 0 ? 'inline-block' : 'none'
}

// Migra entradas viejas (del botón) al formato completo del módulo (port fiel)
function isCorrectedReviewState(value) {
  const normalized = String(value || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/-+/g, '_')
  return normalized === 'aprobado'
}

function reviewStatusBucket(value) {
  const normalized = String(value || 'en_revision').trim().toLowerCase().replace(/\s+/g, '_').replace(/-+/g, '_')
  if (normalized === 'aprobado') return 'corrected'
  if (normalized === 'publicado') return 'published'
  return 'review'
}

function devNormalizeQueue() {
  const l = devQueue.all()
  let changed = false
  l.forEach((it, idx) => {
    if (!it.id) {
      it.id = 'rev_' + (Date.parse(it.ts) || Date.now()).toString(36) + '_' + idx
      changed = true
    }
    if (!it.files) {
      it.files = { 'data.json': JSON.stringify(it.data || {}, null, 2), 'feedback.txt': reviewFeedback(it) || '' }
      if (it.ir) it.files['ir.json'] = JSON.stringify(it.ir, null, 2)
      changed = true
    }
    if (!it.original) {
      it.original = it.files['data.json'] || ''
      changed = true
    }
    if (!it.history) {
      it.history = [{ ts: it.ts, action: 'enviado a revisión' }]
      changed = true
    }
  })
  if (changed) devQueue.saveAll(l)
  return devQueue.all()
}

// helper compartido con AiKnowledgeView (port fiel) — LECCIONES APRENDIDAS:
// misma capa lecQueue sobre localStorage (boston_kb_lecciones_v1); aca se usa
// para capturar "cómo se resolvió" cuando el dev marca corregido.
const lecQueue = {
  KEY: 'boston_kb_lecciones_v1',
  all() {
    try {
      return JSON.parse(localStorage.getItem(this.KEY) || '[]')
    } catch (e) {
      return []
    }
  },
  saveAll(l) {
    localStorage.setItem(this.KEY, JSON.stringify(l))
  },
  add(lec) {
    const l = this.all()
    lec.id = 'lec_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6)
    lec.ts = new Date().toISOString()
    lec.status = 'pendiente'
    l.push(lec)
    this.saveAll(l)
    return lec
  },
  setStatus(id, st) {
    const l = this.all()
    const it = l.find((x) => x.id === id)
    if (it) {
      it.status = st
      this.saveAll(l)
    }
  },
  remove(id) {
    this.saveAll(this.all().filter((x) => x.id !== id))
  },
  approved() {
    return this.all().filter((x) => x.status === 'aprobada')
  },
  pendingCount() {
    return this.all().filter((x) => x.status === 'pendiente').length
  },
}

// Resume un diff de devDiffData en UNA línea ("lo que aplicó", no el script) — port fiel
function lecResumirDiff(diff) {
  if (!diff) return ''
  const p = []
  if (diff.added && diff.added.length) p.push('+campos: ' + diff.added.slice(0, 6).join(','))
  if (diff.removed && diff.removed.length) p.push('-campos: ' + diff.removed.slice(0, 6).join(','))
  if (diff.changed && diff.changed.length)
    p.push('~' + diff.changed.slice(0, 6).map((c) => c.tag + '(' + c.props.slice(0, 4).join(',') + ')').join(' · '))
  if (diff.topChanged && diff.topChanged.length) p.push('top-level: ' + diff.topChanged.join(','))
  return p.join(' | ').slice(0, 400)
}

// Diff campo-a-campo del data.json (original enviado vs editado por el dev) — port fiel
function devDiffData(origStr, curStr) {
  let A, B
  try {
    A = JSON.parse(origStr || '{}')
    B = JSON.parse(curStr || '{}')
  } catch (e) {
    return null
  }
  const fieldsOf = (d) => {
    const m = {}
    ;(d.forms || []).forEach((f) =>
      (f.remarks || []).forEach((r, i) => {
        m[r.tag || r.campoForm || 'idx_' + i] = r
      }),
    )
    return m
  }
  const fa = fieldsOf(A)
  const fb = fieldsOf(B)
  const added = Object.keys(fb).filter((k) => !(k in fa))
  const removed = Object.keys(fa).filter((k) => !(k in fb))
  const changed = []
  Object.keys(fb)
    .filter((k) => k in fa)
    .forEach((k) => {
      const props = []
      const all = new Set(Object.keys(fa[k] || {}).concat(Object.keys(fb[k] || {})))
      all.forEach((p) => {
        if (JSON.stringify(fa[k][p]) !== JSON.stringify(fb[k][p])) props.push(p)
      })
      if (props.length) changed.push({ tag: k, props })
    })
  const topChanged = []
  new Set(Object.keys(A).concat(Object.keys(B))).forEach((k) => {
    if (k === 'forms') return
    if (JSON.stringify(A[k]) !== JSON.stringify(B[k])) topChanged.push(k)
  })
  return { added, removed, changed, topChanged }
}

function estadoBadge(e) {
  if (e === 'publicado') return <span className="bia-badge ok">Publicado</span>
  if (e === 'aprobado') return <span className="bia-badge ok">✓ Marcado corregido</span>
  if (e === 'rechazado') return <span className="bia-badge danger">Rechazado</span>
  if (e === 'editado') return <CBadge color="info">✏ editado</CBadge>
  return <span className="bia-badge warn">⏳ En revisión</span>
}

const fmt = (ts) => {
  try {
    return new Date(ts).toLocaleString()
  } catch (e) {
    return ts || ''
  }
}

function parseMaybeJSON(value, fallback = null) {
  if (!value) return fallback
  if (typeof value === 'object') return value
  try {
    return JSON.parse(value)
  } catch {
    return fallback
  }
}

function firstText(...values) {
  for (const value of values) {
    const text = String(value === undefined || value === null ? '' : value).trim()
    if (text) return text
  }
  return ''
}

function parseMaybeJson(value) {
  if (!value) return null
  if (typeof value === 'object') return value
  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch (_) {
      return value
    }
  }
  return value
}

function normalizeHistory(rawHistory) {
  const history = parseMaybeJson(rawHistory)
  if (!history) return []
  if (Array.isArray(history)) return history
  if (Array.isArray(history.events)) return history.events
  if (history.source || history.target || history.action) return [history]
  return []
}

function getReviewTarget(rawHistory) {
  const history = parseMaybeJson(rawHistory)
  if (!history || Array.isArray(history)) return null
  return history.target || null
}

function getHistoryFeedback(rawHistory) {
  const history = parseMaybeJson(rawHistory)
  if (!history) return ''
  if (typeof history === 'string') return ''
  if (Array.isArray(history)) {
    for (const entry of history) {
      const feedback = getHistoryFeedback(entry)
      if (feedback) return feedback
    }
    return ''
  }
  if (Array.isArray(history.events)) {
    const feedback = getHistoryFeedback(history.events)
    if (feedback) return feedback
  }
  const direct = history.feedback || history.reviewFeedback || history.motivo
  if (direct) return String(direct).trim()
  const target = history.target || {}
  const targetFeedback = target.feedback || target.reviewFeedback || target.motivo
  return targetFeedback ? String(targetFeedback).trim() : ''
}

function reviewFeedback(item) {
  return String(item?.feedback || item?.reviewFeedback || getHistoryFeedback(item?.history) || '').trim()
}

function appendHistory(rawHistory, entry) {
  return normalizeHistory(rawHistory).concat(entry)
}

function slugScriptName(value) {
  return (
    String(value || 'script_revision')
      .toLowerCase()
      .replace(/[^a-z0-9-_]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 80) || 'script_revision'
  )
}

function reviewAssignment(item) {
  const data = parseMaybeJSON(item && item.files && item.files['data.json'], {}) || {}
  const configApp = parseMaybeJSON(item && item.files && item.files['config_app.json'], {}) || {}
  const config = configApp.config || {}
  const session = data._session || {}
  const configSession = config._session || config.session || {}
  const target = getReviewTarget(item?.history) || normalizeHistory(item?.history).find((entry) => entry && entry.target)?.target || {}
  const source = reviewSource(item).toLowerCase()
  const username = firstText(
    item?.username,
    data.username,
    session.username,
    config.username,
    configSession.username,
    target.username,
    target.user,
    /dashboard/.test(source) ? item?.createdUser : '',
  )
  const parentPcc = firstText(
    item?.parentPcc,
    item?.parent_pcc,
    data.parentPcc,
    data.parent_pcc,
    data.parentPCC,
    session.parentPcc,
    session.parent_pcc,
    config.parentPcc,
    config.parent_pcc,
    config.ppcc,
    configSession.parentPcc,
    configSession.parent_pcc,
    target.parent_pcc,
    target.parentPcc,
  )
  const pcc = firstText(
    item?.pcc,
    data.pcc,
    data.pccs,
    session.pcc,
    config.pcc,
    configSession.pcc,
    target.pcc,
  )
  const explicitAssignmentType = firstText(target.assignmentType, target.assignment_type, item?.assignmentType, item?.assignment_type)
  return {
    username,
    parentPcc,
    pcc,
    assignmentType: explicitAssignmentType || (username ? 'user' : ''),
    preferredLanguage: target.preferred_language || target.preferredLanguage || '',
  }
}

function reviewSource(item) {
  const direct = String(item?.source || item?.origin || '').trim()
  if (direct) return direct
  const history = normalizeHistory(item?.history)
  const entry = history.find((row) => row && row.source)
  return String(entry?.source || '').trim()
}

function reviewPublishAssignmentType(item) {
  const assignment = reviewAssignment(item)
  const explicit = String(assignment.assignmentType || '').toLowerCase()
  if (explicit === 'user') {
    return assignment.username && assignment.parentPcc && assignment.pcc ? 'user' : ''
  }
  if (explicit === 'pcc' || explicit === 'parent_pcc') {
    return assignment.parentPcc && assignment.pcc ? 'pcc' : ''
  }
  if (assignment.username && assignment.parentPcc && assignment.pcc) return 'user'
  if (assignment.parentPcc && assignment.pcc && !assignment.username) return 'pcc'
  return ''
}

function reviewPublishAssignment(item) {
  const assignment = reviewAssignment(item)
  const assignmentType = reviewPublishAssignmentType(item)
  const base = {
    assignmentType: assignmentType || null,
    username: null,
    parentPcc: null,
    parent_pcc: null,
    pcc: null,
    preferredLanguage: assignment.preferredLanguage || 'es',
  }
  if (assignmentType === 'user') {
    return {
      ...base,
      username: assignment.username,
      parentPcc: assignment.parentPcc,
      parent_pcc: assignment.parentPcc,
      pcc: assignment.pcc,
    }
  }
  if (assignmentType === 'pcc') {
    return {
      ...base,
      parentPcc: assignment.parentPcc,
      parent_pcc: assignment.parentPcc,
      pcc: assignment.pcc,
    }
  }
  return base
}

function reviewAgency(item) {
  const data = parseMaybeJSON(item && item.files && item.files['data.json'], {}) || {}
  const configApp = parseMaybeJSON(item && item.files && item.files['config_app.json'], {}) || {}
  const config = configApp.config || {}
  const session = data._session || {}
  const configSession = config._session || config.session || {}
  const target = getReviewTarget(item?.history) || normalizeHistory(item?.history).find((entry) => entry && entry.target)?.target || {}
  return firstText(
    item?.agency,
    item?.agencyName,
    item?.nameAgency,
    item?.name_agency,
    data.agency,
    data.agencyName,
    data.nameAgency,
    session.agency,
    session.agencyName,
    session.nameAgency,
    session.name_agency,
    session.name,
    config.agency,
    config.agencyName,
    config.nameAgency,
    config.name,
    configSession.agency,
    configSession.agencyName,
    configSession.nameAgency,
    configSession.name,
    target.agency,
    target.agencyName,
    target.nameAgency,
    target.name_agency,
  )
}

function reviewOriginDetails(item) {
  const assignment = reviewAssignment(item)
  return {
    source: reviewSource(item),
    username: assignment.username,
    parentPcc: assignment.parentPcc,
    pcc: assignment.pcc,
    agency: reviewAgency(item),
  }
}

function sourceLabel(value) {
  const source = String(value || '').toLowerCase()
  if (source === 'dashboard_review') return 'Dashboard'
  if (source === 'dashboard_direct') return 'Dashboard'
  if (source === 'admin_v2') return 'Admin'
  if (source === 'review_dev') return 'Revision Dev'
  return value || 'Origen no identificado'
}

function reviewOriginRows(origin) {
  return [
    ['Origen', sourceLabel(origin?.source)],
    ['Agencia', origin?.agency || 'No disponible'],
    ['Usuario', origin?.username || 'No disponible'],
    ['Parent PCC', origin?.parentPcc || 'No disponible'],
    ['PCC', origin?.pcc || 'No disponible'],
  ]
}

function publishFilesMap(files = []) {
  return Object.fromEntries((files || []).map((file) => [file.path, file.content]))
}

function publishConfigRuntimeEnabled(files = []) {
  try {
    const filesMap = publishFilesMap(files)
    const config = parseMaybeJSON(filesMap['config_app.json'], {})?.config || {}
    return config.enableRuntimeApi === true
  } catch (_) {
    return false
  }
}

function sourceResponseBytes(downloaded) {
  const binary = window.atob(downloaded.contentBase64 || '')
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
  return bytes
}

function downloadSourceResponse(downloaded, fallbackName) {
  const bytes = sourceResponseBytes(downloaded)
  const blob = new Blob([bytes], { type: downloaded.contentType || 'application/octet-stream' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = downloaded.originalFilename || fallbackName || 'archivo-original'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function sourceStatusBadge(status) {
  const options = {
    pending: ['Pendiente', 'secondary'],
    review: ['En revisión', 'warning'],
    published: ['Publicado', 'success'],
    orphan: ['Huérfano', 'danger'],
  }
  const [label, color] = options[status] || [status || 'Sin estado', 'secondary']
  return <CBadge color={color}>{label}</CBadge>
}

function SourceFilesLibrary({ toast, confirm, onOpenReview, onDeleted }) {
  const emptyFilters = { search: '', extension: '', parentPcc: '', pcc: '', status: '', dateFrom: '', dateTo: '' }
  const [filters, setFilters] = useState(emptyFilters)
  const [query, setQuery] = useState(emptyFilters)
  const [result, setResult] = useState({ items: [], page: 1, pageSize: 25, total: 0 })
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [downloadingId, setDownloadingId] = useState('')
  const [downloadingPackageId, setDownloadingPackageId] = useState('')
  const [deletingId, setDeletingId] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError('')
    biaListSourceFiles({ ...query, page, pageSize: 25 })
      .then((data) => {
        if (!alive) return
        setResult(data)
        const lastPage = Math.max(1, Math.ceil(data.total / Math.max(1, data.pageSize)))
        if (page > lastPage) setPage(lastPage)
      })
      .catch((requestError) => { if (alive) setError(requestError?.message || 'No se pudieron consultar los archivos originales.') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [query, page, refreshKey])

  const totalPages = Math.max(1, Math.ceil(result.total / Math.max(1, result.pageSize)))
  const inputStyle = { width: '100%', minWidth: 0, border: '1px solid var(--border)', borderRadius: 5, padding: '7px 8px', background: 'var(--bg)' }
  const setFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }))
  const groupedItems = result.items.reduce((groups, file) => {
    const groupId = file.importId || `legacy-${file.id}`
    if (!groups[groupId]) groups[groupId] = []
    groups[groupId].push(file)
    return groups
  }, {})

  async function download(file) {
    setDownloadingId(file.id)
    try {
      const response = await biaDownloadSourceFile(file.id, file.reviewId || '')
      downloadSourceResponse(response, file.originalFilename)
    } catch (downloadError) {
      toast('No se pudo descargar el original: ' + (downloadError?.message || String(downloadError)), 'danger')
    } finally {
      setDownloadingId('')
    }
  }

  async function downloadPackage(importId) {
    if (!importId || String(importId).startsWith('legacy-')) return
    setDownloadingPackageId(importId)
    try {
      const packageFiles = []
      let packagePage = 1
      let packageTotal = 0
      do {
        const pageData = await biaListSourceFiles({ importId, page: packagePage, pageSize: 100 }) // eslint-disable-line no-await-in-loop
        packageFiles.push(...pageData.items)
        packageTotal = pageData.total
        if (!pageData.items.length) break
        packagePage += 1
      } while (packageFiles.length < packageTotal)
      const { default: JSZip } = await import('jszip')
      const zip = new JSZip()
      const folder = zip.folder(`import-${importId}`)
      const names = new Set()
      for (const file of packageFiles) {
        const response = await biaDownloadSourceFile(file.id, file.reviewId || '') // eslint-disable-line no-await-in-loop
        let name = response.originalFilename || file.originalFilename || file.id
        if (names.has(name.toLowerCase())) name = `${String(file.id).slice(0, 8)}-${name}`
        names.add(name.toLowerCase())
        folder.file(name, sourceResponseBytes(response))
      }
      const blob = await zip.generateAsync({ type: 'blob' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `import-${importId}.zip`
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (packageError) {
      toast('No se pudo descargar el paquete: ' + (packageError?.message || String(packageError)), 'danger')
    } finally {
      setDownloadingPackageId('')
    }
  }

  async function remove(file) {
    const relationship = file.reviewTitle || file.reviewId || file.scriptUuid
      ? ' Está relacionado con una revisión o script; esa relación permanecerá, pero el original dejará de estar disponible.'
      : ''
    const accepted = await confirm({
      title: 'Eliminar archivo original',
      body: `Se eliminarán definitivamente “${file.originalFilename}”, su manifiesto en S3 y el registro de la tabla.${relationship}`,
      confirmText: 'Eliminar definitivamente',
      cancelText: 'Cancelar',
      tone: 'danger',
    })
    if (!accepted) return
    setDeletingId(file.id)
    try {
      await biaDeleteSourceFile(file.id)
      toast(`Archivo original “${file.originalFilename}” eliminado.`, 'success')
      if (onDeleted) await onDeleted()
      setRefreshKey((value) => value + 1)
    } catch (deleteError) {
      toast('No se pudo eliminar el archivo original: ' + (deleteError?.message || String(deleteError)), 'danger')
    } finally {
      setDeletingId('')
    }
  }

  return (
    <div className="card-x">
      <div className="bia-card-head">
        <div>
          <h3 style={{ marginBottom: 3 }}>Archivos originales y catálogos</h3>
          <small className="text-muted">Fuentes SST/SSC y catálogos CSV/XLS/XLSX agrupados por importación; la ubicación S3 permanece protegida.</small>
        </div>
        <CBadge color="info">{result.total} archivo(s)</CBadge>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 2fr) repeat(4, minmax(110px, 1fr))', gap: 8, marginTop: 12 }}>
        <input style={inputStyle} value={filters.search} onChange={(event) => setFilter('search', event.target.value)} placeholder="Archivo, importId o usuario" />
        <input style={inputStyle} value={filters.parentPcc} onChange={(event) => setFilter('parentPcc', event.target.value.toUpperCase())} placeholder="PPCC" />
        <input style={inputStyle} value={filters.pcc} onChange={(event) => setFilter('pcc', event.target.value.toUpperCase())} placeholder="PCC" />
        <select style={inputStyle} value={filters.extension} onChange={(event) => setFilter('extension', event.target.value)}>
          <option value="">Todos los tipos</option><option value="sst">SST</option><option value="ssc">SSC</option><option value="csv">CSV</option><option value="xls">XLS</option><option value="xlsx">XLSX</option>
        </select>
        <select style={inputStyle} value={filters.status} onChange={(event) => setFilter('status', event.target.value)}>
          <option value="">Todos los estados</option><option value="pending">Pendiente</option><option value="review">En revisión</option><option value="published">Publicado</option><option value="orphan">Huérfano</option>
        </select>
        <input style={inputStyle} type="date" value={filters.dateFrom} onChange={(event) => setFilter('dateFrom', event.target.value)} title="Fecha inicial" />
        <input style={inputStyle} type="date" value={filters.dateTo} onChange={(event) => setFilter('dateTo', event.target.value)} title="Fecha final" />
        <div style={{ gridColumn: 'span 3', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <CButton size="sm" color="light" onClick={() => { setFilters(emptyFilters); setQuery(emptyFilters); setPage(1) }}>Limpiar</CButton>
          <CButton size="sm" color="primary" onClick={() => { setQuery({ ...filters }); setPage(1) }}>Buscar</CButton>
        </div>
      </div>
      {error ? <CAlert color="danger" style={{ marginTop: 12 }}>{error}</CAlert> : null}
      <div style={{ overflowX: 'auto', marginTop: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
            <th style={{ padding: 8 }}>Fecha / archivo</th><th style={{ padding: 8 }}>Origen</th><th style={{ padding: 8 }}>Tipo / integridad</th><th style={{ padding: 8 }}>Relación</th><th style={{ padding: 8 }}>Estado</th><th style={{ padding: 8 }}>Acciones</th>
          </tr></thead>
          <tbody>
            {!loading && Object.entries(groupedItems).map(([importId, packageFiles]) => (
              <React.Fragment key={importId}>
              <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                <td colSpan={6} style={{ padding: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span><strong>Importación:</strong> <code title={importId}>{String(importId).slice(0, 24)}</code> · {packageFiles.length} archivo(s) en esta página</span>
                    {!String(importId).startsWith('legacy-') ? (
                      <CButton size="sm" color="primary" variant="outline" disabled={downloadingPackageId === importId} onClick={() => downloadPackage(importId)}>
                        {downloadingPackageId === importId ? 'Armando ZIP…' : 'Descargar paquete ZIP'}
                      </CButton>
                    ) : null}
                  </div>
                </td>
              </tr>
              {packageFiles.map((file) => (
              <tr key={file.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: 8 }}><strong>{file.originalFilename}</strong><div className="text-muted">{fmt(file.createdDate)} · {Math.max(1, Math.round(Number(file.sizeBytes || 0) / 1024))} KB</div></td>
                <td style={{ padding: 8 }}><strong>{file.parentPcc || '—'} / {file.pcc || '—'}</strong><div className="text-muted">{file.username || file.createdUser || 'Sin usuario'}</div></td>
                <td style={{ padding: 8 }}><CBadge color={file.fileRole === 'catalog' ? 'info' : 'secondary'}>{file.fileRole === 'catalog' ? 'Catálogo' : 'Fuente'}</CBadge><div className="text-muted" title={file.sha256 || ''}>SHA {String(file.sha256 || '').slice(0, 10)}…</div></td>
                <td style={{ padding: 8 }}><div>{file.reviewTitle || (file.reviewId ? 'Revisión vinculada' : 'Sin revisión')}</div><code title={file.scriptUuid || ''}>{String(file.scriptUuid || file.reviewId || '—').slice(0, 13)}</code></td>
                <td style={{ padding: 8 }}>{sourceStatusBadge(file.linkStatus)}</td>
                <td style={{ padding: 8 }}><div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {file.reviewId ? <CButton size="sm" color="light" onClick={() => onOpenReview(file.reviewId)}>Abrir revisión</CButton> : null}
                  <CButton size="sm" color="info" variant="outline" disabled={downloadingId === file.id} onClick={() => download(file)}>{downloadingId === file.id ? 'Descargando…' : 'Descargar'}</CButton>
                  <CButton size="sm" color="danger" variant="outline" disabled={deletingId === file.id} onClick={() => remove(file)}>{deletingId === file.id ? 'Eliminando…' : 'Eliminar'}</CButton>
                </div></td>
              </tr>
              ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {loading ? <p className="bia-empty">Consultando archivos originales…</p> : null}
        {!loading && !result.items.length && !error ? <p className="bia-empty">No hay archivos con esos filtros.</p> : null}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
        <CButton size="sm" color="light" disabled={page <= 1 || loading} onClick={() => setPage((value) => Math.max(1, value - 1))}>Anterior</CButton>
        <small className="text-muted">Página {page} de {totalPages}</small>
        <CButton size="sm" color="light" disabled={page >= totalPages || loading} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}>Siguiente</CButton>
      </div>
    </div>
  )
}

function DevReviewView() {
  const state = useBostonState()
  const { toast, confirm } = useUI()
  const mockModeEnabled = new URLSearchParams(window.location.search).get('mock') === '1'
  const dr = (state.devReview = state.devReview || { sel: null, file: 'data.json' })
  const list = devNormalizeQueue()
  const [reviewStatusFilter, setReviewStatusFilter] = useState('review')
  const [activePanel, setActivePanel] = useState('reviews')
  const [reviewPage, setReviewPage] = useState(1)
  const reviewCount = list.filter((x) => reviewStatusBucket(x.estado) === 'review').length
  const correctedCount = list.filter((x) => reviewStatusBucket(x.estado) === 'corrected').length
  const filteredList = list.filter((x) => reviewStatusBucket(x.estado) === reviewStatusFilter)
  const orderedFilteredList = filteredList.slice().reverse()
  const reviewTotalPages = Math.max(1, Math.ceil(orderedFilteredList.length / DEV_REVIEW_PAGE_SIZE))
  const reviewCurrentPage = Math.min(reviewPage, reviewTotalPages)
  const pagedReviewList = orderedFilteredList.slice((reviewCurrentPage - 1) * DEV_REVIEW_PAGE_SIZE, reviewCurrentPage * DEV_REVIEW_PAGE_SIZE)
  const selectedIsVisible = dr.sel ? filteredList.some((x) => x.id === dr.sel) : false
  const it = dr.sel && selectedIsVisible ? list.find((x) => x.id === dr.sel) || null : null
  const files = it ? Object.keys(it.files || {}) : []
  const cur = it ? (dr.file !== undefined && it.files[dr.file] !== undefined ? dr.file : files[0]) : undefined
  if (it) dr.file = cur

  const [editorValue, setEditorValue] = useState(() => (it && cur !== undefined ? it.files[cur] || '' : ''))
  const [preview, setPreview] = useState(null)
  const [publishFlow, setPublishFlow] = useState({ loading: false, step: '', data: null, error: '' })
  const [clientMessage, setClientMessage] = useState('')
  const [clientMessageBusy, setClientMessageBusy] = useState(false)
  const [queueSyncBusy, setQueueSyncBusy] = useState(false)
  const [queueSyncError, setQueueSyncError] = useState('')
  const [queueCacheTruncated, setQueueCacheTruncated] = useState(false)
  const [queueCacheSummaryOnly, setQueueCacheSummaryOnly] = useState(false)
  const importRef = useRef(null)
  const reviewEnvironment = envConfig()

  useEffect(() => {
    if (reviewPage > reviewTotalPages) setReviewPage(reviewTotalPages)
  }, [reviewPage, reviewTotalPages])

  // Re-sincroniza el editor cuando cambia la seleccion o el archivo activo
  // (en el original el textarea se reconstruia en cada render()).
  useEffect(() => {
    setEditorValue(it && cur !== undefined ? it.files[cur] || '' : '')
    setClientMessage('')
    // eslint-disable-next-line
  }, [dr.sel, cur])

  // El original llamaba updateDevBadge() al final de cada bindDevReview()
  useEffect(() => {
    try {
      updateDevBadge()
    } catch (e) {}
  })

  // SYNC DB → cache local al montar: la cola real vive en ai_review_queue (API);
  // si la API responde, el cache localStorage se reemplaza y el render usa lo fresco.
  const [queueVia, setQueueVia] = useState('localStorage')

  async function refreshDevQueue() {
    if (queueSyncBusy) return
    setQueueSyncBusy(true)
    setQueueSyncError('')
    try {
      const { items, via, warn } = await devQueueList()
      setQueueVia(via)
      if (via === 'db') {
        const persisted = devQueue.replace(items)
        setQueueCacheTruncated(persisted.truncated)
        setQueueCacheSummaryOnly(persisted.summaryOnly === true)
        if (!persisted.ok) {
          setQueueSyncError('La API respondió, pero la cola no cabe en la caché local: ' + persisted.error)
          return
        }
        notifyDevReviewQueueChanged()
        emit()
      } else if (warn) {
        setQueueSyncError('No se pudo consultar la cola en BD: ' + warn)
      }
    } catch (error) {
      setQueueSyncError(error?.message || 'No se pudo actualizar Revisión DEV.')
    } finally {
      setQueueSyncBusy(false)
    }
  }

  useEffect(() => {
    let alive = true
    devQueueList().then(({ items, via, warn }) => {
      if (!alive) return
      setQueueVia(via)
      if (via !== 'db') {
        if (warn) setQueueSyncError('No se pudo consultar la cola en BD: ' + warn)
        return
      }
      const persisted = devQueue.replace(items)
      setQueueCacheTruncated(persisted.truncated)
      setQueueCacheSummaryOnly(persisted.summaryOnly === true)
      if (!persisted.ok) {
        setQueueSyncError('La API respondió, pero la cola no cabe en la caché local: ' + persisted.error)
        return
      }
      notifyDevReviewQueueChanged()
      emit()
    }).catch((error) => {
      if (alive) setQueueSyncError(error?.message || 'No se pudo actualizar Revisión DEV.')
    })
    return () => { alive = false }
    // eslint-disable-next-line
  }, [])

  /* ===== handlers (port de bindDevReview) ===== */

  function selectItem(id) {
    dr.sel = id
    dr.file = 'data.json'
    const item = devQueue.get(id)
    // Anatomía completa SIEMPRE: si la revisión llegó solo con data.json + feedback,
    // genera index.html/app.js/api.js/styles.css/config_app.json desde las plantillas
    // del builder (mismas de la publicación), sin pisar nada ya editado.
    if (item) ensureAnatomyFiles(item).catch(() => {})
    const f = item && item.files && item.files['data.json'] !== undefined ? 'data.json' : item ? Object.keys(item.files || {})[0] : undefined
    setEditorValue(item && f !== undefined ? item.files[f] || '' : '')
    emit()
  }

  function switchFile(f) {
    // Igual que el original: antes de cambiar de archivo persiste lo tipeado en el actual
    const item = devQueue.get(dr.sel)
    if (item && cur !== undefined) {
      item.files[cur] = editorValue
      devQueue.update(item.id, { files: item.files })
    }
    dr.file = f
    setEditorValue(item && item.files[f] !== undefined ? item.files[f] : '')
    emit()
  }

  async function onImportFile(e) {
    const f = e.target.files[0]
    if (!f) return
    try {
      const pkg = JSON.parse(await f.text())
      if (pkg._tipo !== 'boston_revision_v1' || !pkg.files) throw new Error('No es un paquete de revisión válido (falta _tipo/files).')
      const l = devQueue.all()
      const i = l.findIndex((x) => x.id === (pkg.meta && pkg.meta.id))
      if (i >= 0) {
        l[i].files = pkg.files
        l[i].estado = 'editado'
        l[i].history = appendHistory(l[i].history, { ts: new Date().toISOString(), action: 'paquete importado con modificaciones' })
        devQueue.saveAll(l)
        dr.sel = l[i].id
      } else {
        const ne = {
          id: (pkg.meta && pkg.meta.id) || 'rev_' + Date.now().toString(36),
          ts: (pkg.meta && pkg.meta.ts) || new Date().toISOString(),
          title: (pkg.meta && pkg.meta.title) || '(importado)',
          feedback: (pkg.meta && pkg.meta.feedback) || '',
          estado: 'editado',
          published: (pkg.meta && pkg.meta.published) || null,
          files: pkg.files,
          original: pkg.files['data.json'] || '',
          history: [{ ts: new Date().toISOString(), action: 'importado' }],
        }
        l.push(ne)
        devQueue.saveAll(l)
        dr.sel = ne.id
      }
      const item = devQueue.get(dr.sel)
      const f2 = item && item.files && item.files[dr.file] !== undefined ? dr.file : item ? Object.keys(item.files || {})[0] : undefined
      setEditorValue(item && f2 !== undefined ? item.files[f2] || '' : '')
      emit()
    } catch (err) {
      toast('No se pudo importar: ' + err.message, 'error')
    }
    e.target.value = ''
  }

  // helper compartido con EditorView (port fiel): aplica un data.json al Custom Script Builder
  function edApplyScript(data, config, scriptName, appJs, fichaMd, logicJson, options = {}) {
    // FIX preservacion: igual que EditorView — conservar el data.json completo original.
    try {
      state.cs.raw = JSON.parse(JSON.stringify(data))
      if (state.cs.raw && state.cs.raw._uuid !== undefined) delete state.cs.raw._uuid
      if (state.cs.raw && state.cs.raw._session !== undefined) delete state.cs.raw._session
    } catch (e) {
      state.cs.raw = null
    }
    state.cs.name = scriptName || data.name || 'script_editado'
    state.cs.title = data.title || scriptName
    state.cs.validateActiveReservation = data.validateActiveReservation !== false
    state.cs.sendCommands = !!data.sendCommands
    state.cs.orderExecute = data.orderExecute !== false
    state.cs.editCatalogues = !!data.editCatalogues
    state.cs.reintentos = data.reintentos || 0
    state.cs.targetEnv = data.targetEnv || 'stg'
    state.cs.forms =
      Array.isArray(data.forms) && data.forms.length
        ? data.forms.map((f) => ({
            ...f,
            form: f.form || 'form1',
            title: f.title || 'FORM',
            title_visible: f.title_visible !== false,
            remarks: (f.remarks || []).map((r) => ({
              // FIX preservacion: conservar TODAS las claves del remark original
              ...normalizeRemarkField(r),
              campoForm: r.campoForm || '',
              label: r.label || '',
              mandatory: !!r.mandatory,
              remark: r.remark || '',
              remark_mask: r.remark_mask || '',
              source_additional: r.source_additional || '',
              typedata: r.typedata || 'text',
              typeremark: displayRemarkType(r),
              datasource: r.datasource || [],
            })),
          }))
        : [{ form: 'form1', title: 'FORMULARIO', title_visible: true, remarks: [] }]
    state.cs.postCommands = data.postCommands || []
    state.cs.logicJson = logicJson || null
    state.cs.handlerJs = appJs || null // AGENTE 4: handler asociado (se publica como app.js)
    state.cs.fichaMd = fichaMd || null
    state.cs.uuid = (config && config.config && config.config.uuid) || state.cs.uuid || uuidV4()
    state.cs.url = options.url || state.cs.url || ''
    state.cs.config = parseMaybeJSON(options.customScriptConfig, state.cs.config || {})
    state.cs.persisted = options.persisted === true || options.isSaved === true || !!state.cs.url
    state.cs.isSaved = state.cs.persisted
    csReindexTags()
    if (!options.stayInCurrentView) state.view = 'custom'
    emit()
    if (!options.silent) toast(
      `Script "${scriptName}" cargado. UUID: ${state.cs.uuid} · Forms: ${state.cs.forms.length} · Commands: ${state.cs.postCommands.length}. ` +
        'Abrí la pestaña "Custom Script", editá lo que necesites y dale "Guardar + Publicar".',
      'success',
    )
  }

  // helper local: port mínimo de showPreviewModal del original (single-file en iframe,
  // descargar HTML único y abrir en pestaña nueva), con CModal en vez de overlay manual.
  async function showPreviewModal(dataJson, configJson, options = {}) {
  const realPreview = options.realPreview === true
  const env = String(options.env || dataJson?.targetEnv || state.cs.targetEnv || 'stg').toLowerCase().trim()
  const token = state.sessionToken || ''
  const title = (dataJson && dataJson.title) || state.cs.title || 'Custom Script'

  setPreview({
    title,
    dataJson,
    configJson,
    url: null,
    error: null,
    realPreview,
    realArmed: false,
    env,
    tokenPresent: Boolean(token),
    logicJson: options.logicJson || null,
  })

  try {
    // Forzar refresh del cache del runtime para que el preview SIEMPRE use la última app.js de disco
    await csLoadRuntimeTemplates(true)

    let singleHtml = await csBuildSingleFile(dataJson, configJson, {
      previewMode: !realPreview,
      simulateApi: !realPreview,
      realPreview,
      token,
      env,
      title,
      logicJson: options.logicJson || null,
    })

    /**
     * IMPORTANTE:
     * En preview Blob NO se debe abrir:
     * blob:http://localhost:3001/uuid?token=...&env=stg
     *
     * La URL Blob debe abrirse exacta como la devuelve URL.createObjectURL(blob).
     * Por eso inyectamos token/env dentro del HTML.
     */
    if (realPreview) {
      const runtimeContext = {
        token,
        env,
        realPreview: true,
      }

      const contextScript = `
<script>
  window.__BOSTON_RUNTIME_CONTEXT__ = ${JSON.stringify(runtimeContext)};
</script>
`

      if (singleHtml.includes('</head>')) {
        singleHtml = singleHtml.replace('</head>', `${contextScript}</head>`)
      } else {
        singleHtml = `${contextScript}${singleHtml}`
      }
    }

    const blob = new Blob([singleHtml], { type: 'text/html' })
    const baseUrl = URL.createObjectURL(blob)

    // Para Blob, usar SIEMPRE la URL limpia. No agregar query params.
    const url = baseUrl

    console.log('[review preview] blob url', url)
    console.log('[review preview] realPreview', realPreview)
    console.log('[review preview] token present', Boolean(token))
    console.log('[review preview] env', env)

    setPreview((p) => (p ? { ...p, url } : p))
  } catch (e) {
    console.error('[boston] error en preview:', e)
    setPreview((p) => (p ? { ...p, error: e.message || String(e) } : p))
  }
}

  function armRealPreview() {
    if (!preview?.realPreview || preview.realArmed) return
    const env = String(preview.env || '').toLowerCase().trim()
    if (!['stg', 'stage'].includes(env)) {
      toast('La prueba real SST está bloqueada fuera de STG.', 'danger')
      return
    }
    if (!state.sessionToken || !preview.tokenPresent) {
      toast('La sesión ya no tiene token Sabre. Vuelve a abrir el dashboard desde Sabre.', 'warning')
      return
    }
    const accepted = window.confirm(
      'PRUEBA REAL SST EN STG\n\n' +
      'Al continuar, el script podrá enviar comandos reales a la sesión Sabre activa. ' +
      'Usa únicamente una reserva de prueba y verifica el PNR antes de cada acción.\n\n' +
      '¿Deseas iniciar la ejecución real?'
    )
    if (!accepted) return
    setPreview((current) => current ? {
      ...current,
      realArmed: true,
      realStartedAt: new Date().toISOString(),
    } : current)
  }

  function closePreview() {
    if (preview && preview.url) {
      const u = String(preview.url).split('?')[0]
      setTimeout(() => URL.revokeObjectURL(u), 100)
    }
    setPreview(null)
  }

  function downloadPreviewHtml() {
    if (!preview) return
    // Generamos un .html SIN previewMode para el archivo descargable (es el de producción)
    csBuildSingleFile(preview.dataJson, preview.configJson, { previewMode: false, title: preview.title, logicJson: preview.logicJson }).then((prodHtml) => {
      const pblob = new Blob([prodHtml], { type: 'text/html' })
      const purl = URL.createObjectURL(pblob)
      const a = document.createElement('a')
      a.href = purl
      const slug = String(preview.title).toLowerCase().replace(/[^a-z0-9_-]+/g, '_').slice(0, 40) || 'custom_script'
      a.download = `${slug}.html`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(purl), 1000)
    })
  }

  async function persistCurrentEditorFile({ markEdited = true, remote = false } = {}) {
    const item = devQueue.get(dr.sel)
    if (!item || cur === undefined) return item
    const val = editorValue
    if (String(cur).endsWith('.json')) {
      try {
        JSON.parse(val)
      } catch (e) {
        toast('JSON invalido en ' + cur + ':\n' + e.message, 'error')
        throw e
      }
    }
    item.files = item.files || {}
    item.files[cur] = val
    const patch = { files: item.files }
    if (markEdited) {
      item.history = appendHistory(item.history, { ts: new Date().toISOString(), action: 'edito ' + cur })
      patch.history = item.history
    }
    devQueue.update(item.id, patch)
    if (remote) {
      try {
        await devQueueSaveFiles(item)
      } catch (e) {
        toast('El archivo se guardo localmente, pero no se pudo persistir en Revisión Dev: ' + (e.message || String(e)), 'warning')
        throw e
      }
    }
    return item
  }

  async function actSave() {
    try {
      await persistCurrentEditorFile({ markEdited: true, remote: true })
    } catch (_) {
      return
    }
    toast('Archivo guardado.', 'success')
    emit()
  }

  function parseDataJson(item) {
    // Igual que el original: si el archivo activo es data.json usa lo tipeado, si no lo guardado.
    // TOLERANTE a vacio: las reviews creadas por "Reenviar a Dev" (antes del fix de backend)
    // llegan SIN data.json -> JSON.parse('') tiraba "Unexpected end of JSON input" al publicar.
    const raw = String((cur === 'data.json' ? editorValue : (item.files && item.files['data.json'])) || '').trim()
    if (!raw) {
      const err = new Error(
        'Esta revision no trae data.json (llego de un "Reenviar a Dev" sin archivos adjuntos). ' +
        'Opciones: (1) volver a reenviar el script desde el dashboard (con el backend nuevo adjunta el data.json del release publicado), ' +
        'o (2) pegar el data.json en el editor y guardar.'
      )
      err.emptyDataJson = true
      throw err
    }
    return JSON.parse(raw)
  }

  async function actPreview(options = {}) {
    if (options.realPreview !== true && !mockModeEnabled) return
    const item = devQueue.get(dr.sel)
    if (!item) return
    if (options.realPreview && !state.sessionToken) {
      toast('Falta token Sabre para ejecutar comandos reales.', 'warning')
      return
    }
    try {
      const baseDataJson = parseDataJson(item)
      const isSst = String(baseDataJson?.executionMode || '').toLowerCase().trim() === 'sst_interpreter_v1'
      const previewEnv = String(baseDataJson?.targetEnv || state.cs.targetEnv || 'stg').toLowerCase().trim()
      if (options.realPreview && isSst && !['stg', 'stage'].includes(previewEnv)) {
        toast('La prueba real SST sólo está habilitada en STG. El script indica ambiente ' + (previewEnv || '(vacío)') + '.', 'danger')
        return
      }
      applyReviewToBuilder(item, { stayInCurrentView: true, silent: true })
      const payload = await csBuildPublishPayload()
      const filesMap = Object.fromEntries((payload.files || []).map((file) => [file.path, file.content]))
      const dataJson = parseMaybeJSON(filesMap['data.json'], baseDataJson) || baseDataJson
      const configJson =
        parseMaybeJSON(filesMap['config_app.json'], null) ||
        parseMaybeJSON(filesMap['config.json'], null) ||
        csBuildConfigJson({ enableRuntimeApi: true })
      configJson.config = {
        ...(configJson.config || {}),
        uuid: payload.script?.uuid || configJson.config?.uuid || state.cs.uuid,
        useInternalConfig: true,
        enableRuntimeApi: options.realPreview ? true : configJson.config?.enableRuntimeApi === true,
      }
      if (options.realPreview && isSst) {
        // La prueba controlada nunca debe heredar accidentalmente el endpoint PROD.
        configJson.config.env = 'stg'
        configJson.config.publicApiBaseUrl = 'https://stg.boston.basset.ws'
        configJson.config.apiBaseUrl = 'https://stg.boston.basset.ws'
      }
      const logicJson = parseMaybeJSON(filesMap['logic.json'], state.cs.logicJson || null)
      await showPreviewModal(dataJson, configJson, {
        realPreview: options.realPreview === true,
        logicJson,
        env: options.realPreview && isSst ? 'stg' : previewEnv,
      })
      return
    } catch (e) {
      toast('No se pudo armar la vista previa: ' + (e.message || String(e)), 'danger')
      return
    }
  }

  function reviewLogicJson(item, dataJson) {
    const logicJson = parseMaybeJSON(item?.files?.['logic.json'], null)
    if (String(dataJson?.executionMode || '').toLowerCase().trim() === 'sst_interpreter_v1') {
      if (!logicJson || !logicJson.blocks || !Object.keys(logicJson.blocks).length) {
        throw new Error('La revisión SST no contiene un logic.json ejecutable. Reenvía el SST desde el importador actualizado.')
      }
    }
    return logicJson
  }

  function reviewSstSimulationIR(item, dataJson, logicJson) {
    const storedIR = parseMaybeJSON(item?.files?.['ir.json'], null)
    const canonical = dataJson?.sstRuntime?.data && typeof dataJson.sstRuntime.data === 'object'
      ? dataJson.sstRuntime.data
      : {}
    const collectionValues = (value, keyName) => {
      if (Array.isArray(value)) return value
      if (!value || typeof value !== 'object') return []
      return Object.entries(value).map(([key, entry]) => ({
        ...(entry && typeof entry === 'object' ? entry : {}),
        [keyName]: entry?.[keyName] || key,
      }))
    }
    const logicForms = collectionValues(logicJson?.forms, 'id')
    const logicMenus = collectionValues(logicJson?.menus, 'id')
    const storedForms = Array.isArray(storedIR?.forms) ? storedIR.forms : []
    const storedMenus = Array.isArray(storedIR?.menus) ? storedIR.menus : []
    const storedMenuTree = Array.isArray(storedIR?.menuTree) ? storedIR.menuTree : []
    const canonicalForms = Array.isArray(canonical.forms) ? canonical.forms : []
    const forms = storedForms.length ? storedForms : logicForms.length ? logicForms : canonicalForms
    const menus = storedMenus.length ? storedMenus : logicMenus
    return {
      ...canonical,
      ...(storedIR || {}),
      title: storedIR?.title || canonical.title || dataJson?.title || item?.title || 'Script SST',
      forms,
      menus,
      menuTree: storedMenuTree.length ? storedMenuTree : menus,
    }
  }

  function actSstSimulation() {
    if (!mockModeEnabled) return
    const item = devQueue.get(dr.sel)
    if (!item) return
    try {
      syncCurrentEditor(item)
      const dataJson = parseDataJson(item)
      if (String(dataJson?.executionMode || '').toLowerCase().trim() !== 'sst_interpreter_v1') {
        throw new Error('La revisión seleccionada no usa el intérprete SST.')
      }
      const logicJson = reviewLogicJson(item, dataJson)
      const ir = reviewSstSimulationIR(item, dataJson, logicJson)
      setPreview({
        title: dataJson.title || item.title || 'Script SST',
        dataJson,
        configJson: reviewConfig(item, item.scriptUuid || item._uuid || ''),
        logicJson,
        ir,
        url: null,
        error: null,
        realPreview: false,
        sstSimulation: true,
      })
    } catch (error) {
      toast('No se pudo iniciar la ejecución simulada SST: ' + (error.message || String(error)), 'danger')
    }
  }

  function actBuilder() {
    const item = devQueue.get(dr.sel)
    if (!item) return
    let dj
    try {
      dj = parseDataJson(item)
    } catch (e) {
      toast('data.json inválido: ' + e.message, 'error')
      return
    }
    let logicJson
    try {
      logicJson = reviewLogicJson(item, dj)
    } catch (e) {
      toast(e.message, 'error')
      return
    }
    // FIX: preservar el uuid publicado del item (o memoizar uno nuevo) para no pisar otro script
    const revUuid = item.scriptUuid || (item.published && item.published.uuid) || item._uuid || (item._uuid = uuidV4())
    edApplyScript(
      dj,
      { config: { uuid: revUuid, useInternalConfig: true } },
      slugScriptName(dj.title || 'rev'),
      (item.files && item.files['app.js']) || null,
      (item.files && item.files['FICHA.md']) || null,
      logicJson,
    )
  }

  function syncCurrentEditor(item) {
    if (item && cur !== undefined) {
      item.files = item.files || {}
      item.files[cur] = editorValue
    }
  }

  function reviewConfig(item, revUuid) {
    const filesMap = (item && item.files) || {}
    const fileConfig = parseMaybeJSON(filesMap['config_app.json'], null) || parseMaybeJSON(filesMap['config.json'], null) || {}
    return {
      ...fileConfig,
      config: {
        ...(fileConfig.config || {}),
        uuid: (fileConfig.config && fileConfig.config.uuid) || revUuid,
        useInternalConfig: true,
      },
    }
  }

  function applyReviewToBuilder(item, options = {}) {
    if (!item) throw new Error('Selecciona un item de Revisión Dev.')
    syncCurrentEditor(item)
    const dataJson = parseDataJson(item)
    const revUuid =
      item.scriptUuid ||
      (item.published && item.published.uuid) ||
      (item.files && parseMaybeJSON(item.files['config_app.json'], {})?.config?.uuid) ||
      item._uuid ||
      (item._uuid = uuidV4())
    const configJson = reviewConfig(item, revUuid)
    const logicJson = reviewLogicJson(item, dataJson)
    const scriptName = slugScriptName(dataJson.name || dataJson.title || item.title || 'revision')
    edApplyScript(
      dataJson,
      configJson,
      scriptName,
      (item.files && item.files['app.js']) || null,
      (item.files && item.files['FICHA.md']) || null,
      logicJson,
      { stayInCurrentView: true, silent: true, ...options },
    )
    return { dataJson, configJson, scriptName, uuid: state.cs.uuid }
  }

  function buildDraftPayload(item) {
    const logicJson = state.cs.logicJson || { postCommands: state.cs.postCommands || [] }
    const assignment = reviewPublishAssignment(item)
    return {
      source: 'review_dev',
      reviewId: item.id,
      id: item.id,
      scriptUuid: state.cs.uuid,
      script_uuid: state.cs.uuid,
      script: {
        uuid: state.cs.uuid,
        name: state.cs.name,
        description: state.cs.description || '',
        url: state.cs.url || '',
        status: 0,
      },
      config: {
        dataJson: csBuildDataJson(),
        configJson: csBuildConfigJson(),
        logicJson,
        handlerJs: state.cs.handlerJs || null,
        fichaMd: state.cs.fichaMd || null,
      },
      username: assignment.username || null,
      parentPcc: assignment.parentPcc || null,
      parent_pcc: assignment.parentPcc || null,
      pcc: assignment.pcc || null,
      assignmentType: assignment.assignmentType || null,
      preferredLanguage: assignment.preferredLanguage || 'es',
      status: 0,
      updatedUser: 'admin_v2',
      history: item.history || [],
    }
  }

  function updateLibraryPublished(patch) {
    const item = (state.library || []).find((entry) => entry.uuid === state.cs.uuid)
    if (!item) return
    Object.assign(item, patch)
    Storage.saveLibrary(state.library)
  }

  async function saveReviewToLibrary({ silent = false } = {}) {
    const item = devQueue.get(dr.sel)
    if (!item) throw new Error('Selecciona un item de Revisión Dev.')
    setPublishFlow({ loading: true, step: 'save-draft', data: null, error: '' })
    try {
      await persistCurrentEditorFile({ markEdited: false, remote: true })
      applyReviewToBuilder(item)
      const response = await biaSaveDraftCustomScript(buildDraftPayload(item))
      if (response?.ok === false) throw new Error(response.message || 'No se pudo guardar en Biblioteca.')
      const localResult = await csPersistAndPublish()

      state.cs.persisted = true
      state.cs.isSaved = true
      updateLibraryPublished({
        persisted: true,
        isSaved: true,
        config: response?.config || state.cs.config || {},
      })

      const published = {
        ...(item.published || {}),
        uuid: state.cs.uuid,
        name: state.cs.name,
        persisted: true,
        savedAt: new Date().toISOString(),
      }
      item.history = appendHistory(item.history, { ts: new Date().toISOString(), action: 'guardado en Biblioteca/custom_scripts' })
      devQueue.update(item.id, { files: item.files, published, history: item.history })
      emit()

      const data = { saveDraft: response, libraryItem: localResult.item }
      setPublishFlow({ loading: false, step: 'save-draft', data, error: '' })
      if (!silent) toast('Script guardado en Biblioteca y custom_scripts.', 'success')
      return data
    } catch (e) {
      const error = e.message || String(e)
      setPublishFlow({ loading: false, step: 'save-draft', data: null, error })
      if (!silent) toast(`Error guardando en Biblioteca: ${error}`, 'danger')
      throw e
    }
  }

  async function validateReviewPublish() {
    const item = devQueue.get(dr.sel)
    if (!item) return
    setPublishFlow({ loading: true, step: 'validate-publish', data: null, error: '' })
    try {
      applyReviewToBuilder(item)
      const payload = await csBuildPublishPayload()
      const reviewScriptUuid =
        item.scriptUuid ||
        (item.published && item.published.uuid) ||
        (item.files && parseMaybeJSON(item.files['config_app.json'], {})?.config?.uuid) ||
        payload.script?.uuid ||
        state.cs.uuid
      const assignment = reviewPublishAssignment(item)
      const response = await biaValidateCustomScriptPublish({
        ...payload,
        source: 'review_dev',
        reviewId: item.id,
        id: item.id,
        scriptUuid: reviewScriptUuid,
        script_uuid: reviewScriptUuid,
        estado: item.estado,
        assignmentType: assignment.assignmentType || null,
        preferredLanguage: assignment.preferredLanguage || 'es',
        username: assignment.username || null,
        parentPcc: assignment.parentPcc || null,
        parent_pcc: assignment.parentPcc || null,
        pcc: assignment.pcc || null,
        script: {
          ...payload.script,
          uuid: reviewScriptUuid || payload.script.uuid,
        },
      })
      if (response?.ok === false) throw new Error(response.message || 'No se pudo validar el paquete.')
      setPublishFlow({ loading: false, step: 'validate-publish', data: response, error: '' })
      toast('Paquete validado correctamente.', 'success')
    } catch (e) {
      const error = e.message || String(e)
      setPublishFlow({ loading: false, step: 'validate-publish', data: null, error })
      toast(`Error validando paquete: ${error}`, 'danger')
    }
  }

  async function approveAndPublish() {
    const item = devQueue.get(dr.sel)
    if (!item) return
    if (!isCorrectedReviewState(item.estado)) {
      toast('No se puede publicar el script hasta que este marcado como corregido.', 'warning')
      return
    }
    const reviewScriptUuid =
      item.scriptUuid ||
      (item.published && item.published.uuid) ||
      (item.files && parseMaybeJSON(item.files['config_app.json'], {})?.config?.uuid) ||
      state.cs.uuid

    const ok = await confirm({
      title: 'Aprobar y publicar',
      body: 'Se guardara en Biblioteca, se validara el paquete y se publicara en el ambiente actual configurado en backend.\n\nSi S3 falla, el item permanece en Revision Dev.',
      confirmText: 'Publicar en ambiente actual',
      cancelText: 'Cancelar',
      tone: 'warning',
    })
    if (!ok) return

    setPublishFlow({ loading: true, step: 'publish-release', data: null, error: '' })
    try {
      await saveReviewToLibrary({ silent: true })
      const payload = await csBuildPublishPayload()
      const filesMap = publishFilesMap(payload.files)
      console.log('[review publish] source review_dev')
      console.log('[review publish] scriptUuid', reviewScriptUuid || payload.script.uuid)
      console.log('[review publish] using library builder', true)
      console.log('[review publish] files', Object.keys(filesMap))
      console.log('[review publish] has app.js', Boolean(filesMap['app.js']))
      console.log('[review publish] has api.js', Boolean(filesMap['api.js']))
      console.log('[review publish] has data.json', Boolean(filesMap['data.json']))
      console.log('[review publish] has logic.json', Boolean(filesMap['logic.json']))
      console.log('[review publish] has config_app.json', Boolean(filesMap['config_app.json']))
      console.log('[review publish] enableRuntimeApi', publishConfigRuntimeEnabled(payload.files))
      const assignment = reviewPublishAssignment(item)
      const dataJsonTargetEnv = parseMaybeJSON(filesMap['data.json'], {})?.targetEnv
      const publishTargetEnv = resolveReviewPublishTargetEnv(window.location.href, state.cs.targetEnv, dataJsonTargetEnv)
      const publishPayload = {
        ...payload,
        targetEnv: publishTargetEnv,
        source: 'review_dev',
        reviewId: item.id,
        id: item.id,
        scriptUuid: reviewScriptUuid,
        script_uuid: reviewScriptUuid,
        estado: item.estado,
        assignmentType: assignment.assignmentType || null,
        preferredLanguage: assignment.preferredLanguage || 'es',
        username: assignment.username || null,
        parentPcc: assignment.parentPcc || null,
        parent_pcc: assignment.parentPcc || null,
        pcc: assignment.pcc || null,
        script: {
          ...payload.script,
          uuid: reviewScriptUuid || payload.script.uuid,
          targetEnv: publishTargetEnv,
        },
      }
      const validation = await biaValidateCustomScriptPublish(publishPayload)
      if (validation?.ok === false) throw new Error(validation.message || 'No se pudo validar el paquete.')
      const publishResponse = await biaPublishReleaseCustomScript(publishPayload)
      if (publishResponse?.ok === false) throw new Error(publishResponse.message || 'No se pudo publicar en S3.')
      console.log('[review publish] s3 url', publishResponse.url)

      const publish = {
        published: true,
        provider: publishResponse.provider || 's3',
        targetEnv: publishResponse.targetEnv || '',
        url: publishResponse.url,
        releaseId: publishResponse.releaseId,
        releaseTag: publishResponse.releaseTag,
        commitSha: publishResponse.commitSha,
        releaseUrl: publishResponse.releaseUrl,
        publishedAt: publishResponse.publishedAt || new Date().toISOString(),
        publishedBy: publishResponse.publishedBy || 'admin_v2',
      }
      state.cs.url = publish.url || state.cs.url || ''
      state.cs.config = {
        ...parseMaybeJSON(state.cs.config, {}),
        publish,
      }
      state.cs.persisted = true
      state.cs.isSaved = true
      updateLibraryPublished({
        url: state.cs.url,
        config: state.cs.config,
        persisted: true,
        isSaved: true,
      })

      item.history = appendHistory(item.history, { ts: new Date().toISOString(), action: 'aprobado y publicado en ambiente actual' })
      devQueue.update(item.id, {
        estado: 'publicado',
        published: {
          ...(item.published || {}),
          ...publish,
          uuid: state.cs.uuid,
          name: state.cs.name,
          persisted: true,
        },
        history: item.history,
      })
      emit()
      setPublishFlow({ loading: false, step: 'publish-release', data: publishResponse, error: '' })
      toast('Script aprobado y publicado en el ambiente actual.', 'success')
    } catch (e) {
      const error = e.message || String(e)
      setPublishFlow({ loading: false, step: 'publish-release', data: null, error })
      toast(`No se pudo publicar: ${error}. El item se conserva en Revision Dev.`, 'danger')
    }
  }

  function actExport() {
    const item = devQueue.get(dr.sel)
    if (!item) return
    const pkg = {
      _tipo: 'boston_revision_v1',
      meta: { id: item.id, title: item.title, ts: item.ts, estado: item.estado, feedback: item.feedback, published: item.published || null },
      files: item.files,
    }
    const blob = new Blob([JSON.stringify(pkg, null, 2)], { type: 'application/json' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'revision_' + String(item.title || 'script').toLowerCase().replace(/[^a-z0-9-_]+/g, '_') + '.json'
    a.click()
    URL.revokeObjectURL(a.href)
  }

  /* Descarga TODO el paquete del script en un ZIP: los archivos de la revisión (DB:
     data.json, ir.json, app.js, FICHA.md, feedback) + TODOS los archivos reales
     publicados en S3 (index.html, app.js, api.js, styles.css, data.json, catalogs.json,
     logic.json, config_app.json, FICHA.md). Los fetch a S3 son best-effort: lo que falle queda
     anotado en manifest.txt. */
  /* Resuelve la URL publicada del script y baja TODOS los archivos de su carpeta
     S3 (index.html + assets referenciados + set conocido). Devuelve { base, files }. */
  async function fetchPublishedS3Files(item) {
    const uuid = String((item.published && item.published.uuid) || item.scriptUuid || '').trim()
    if (!uuid) throw new Error('El item no tiene script publicado asociado (falta uuid).')
    const catalog = await scriptsCatalog()
    const rows = Array.isArray(catalog) ? catalog : (catalog && (catalog.scripts || catalog.customScripts || catalog.data)) || []
    const found = rows.find((s) => String(s.uuid || '').trim() === uuid)
    const url = String((found && found.url) || '').trim()
    if (!url) throw new Error('No se pudo resolver la URL publicada del script.')
    const base = url.split('?')[0].replace(/\/[^/]*$/, '')
    const files = {}
    const grabbed = new Set()
    const fetchOne = async (name) => {
      const rel = String(name).replace(/^\.?\//, '').split('?')[0].split('#')[0]
      if (!rel || grabbed.has(rel) || rel.includes('..')) return null
      grabbed.add(rel)
      try {
        const r = await fetch(base + '/' + rel, { cache: 'no-store' })
        if (r.ok) { const text = await r.text(); files[rel] = text; return text }
      } catch (e) { console.warn('[s3 fetch]', rel, e.message) }
      return null
    }
    const indexHtml = await fetchOne('index.html')
    if (indexHtml) {
      const refs = new Set()
      const rx = /(?:src|href)\s*=\s*["']([^"']+)["']/gi
      let m
      while ((m = rx.exec(indexHtml)) !== null) {
        const ref = m[1].trim()
        if (ref && !/^(https?:)?\/\//i.test(ref) && !ref.startsWith('data:') && !ref.startsWith('#') && !ref.startsWith('/')) refs.add(ref)
      }
      for (const ref of refs) await fetchOne(ref) // eslint-disable-line no-await-in-loop
    }
    for (const f of ['app.js', 'api.js', 'styles.css', 'data.json', 'catalogs.json', 'logic.json', 'config_app.json', 'FICHA.md']) {
      await fetchOne(f) // eslint-disable-line no-await-in-loop
    }
    return { base, files }
  }

  /* Trae los archivos publicados al EDITOR del dev (solo agrega los que faltan:
     lo ya editado en la revisión — data.json, app.js — no se pisa). */
  async function actLoadS3ToEditor() {
    const item = devQueue.get(dr.sel)
    if (!item) return
    toast('Trayendo los archivos publicados al editor…', 'info')
    try {
      const { files } = await fetchPublishedS3Files(item)
      const names = Object.keys(files)
      if (!names.length) throw new Error('No se pudo bajar ningún archivo de la carpeta publicada.')
      item.files = item.files || {}
      const added = []
      const kept = []
      names.forEach((name) => {
        if (item.files[name] === undefined) { item.files[name] = files[name]; added.push(name) }
        else kept.push(name)
      })
      item.history = appendHistory(item.history, { ts: new Date().toISOString(), action: 'cargó al editor los archivos publicados (' + names.length + ')' })
      devQueue.update(item.id, { files: item.files, history: item.history })
      emit()
      toast('Editor actualizado. Agregados: ' + (added.join(', ') || 'ninguno') + (kept.length ? ' · Ya editados (se conservan): ' + kept.join(', ') : ''), 'success')
    } catch (e) {
      toast('No se pudieron traer los archivos: ' + e.message, 'danger')
    }
  }

  /* Republica TODOS los archivos del editor TAL CUAL (sin regenerar templates):
     release nuevo en S3 con las modificaciones manuales del dev. Requiere estar
     marcado corregido (misma regla que Aprobar y publicar). */
  const RUNTIME_PUBLISH_KEYS = ['index.html', 'app.js', 'api.js', 'styles.css', 'data.json', 'catalogs.json', 'logic.json', 'config_app.json', 'FICHA.md']
  async function actRepublishAsIs() {
    const item = devQueue.get(dr.sel)
    if (!item) return
    try {
      await persistCurrentEditorFile({ markEdited: false, remote: true })
    } catch (_) {
      return
    }
    await ensureAnatomyFiles(item)
    devQueue.update(item.id, { files: item.files, history: item.history })
    const filesMap = item.files || {}
    const present = RUNTIME_PUBLISH_KEYS.filter((k) => filesMap[k] !== undefined && String(filesMap[k]).trim() !== '')
    if (!present.includes('index.html')) {
      toast('Falta index.html en el editor. Usá primero “Cargar S3 en editor” para traer la carpeta publicada.', 'warning')
      return
    }
    if (!isCorrectedReviewState(item.estado)) {
      toast('Primero marcá el item como corregido (✓): la publicación exige revisión aprobada.', 'warning')
      return
    }
    const reviewScriptUuid = item.scriptUuid || (item.published && item.published.uuid) || parseMaybeJSON(filesMap['config_app.json'], {})?.config?.uuid || ''
    const ok = await confirm({
      title: 'Republicar archivos tal cual',
      body: 'Se subirán ' + present.length + ' archivo(s) EXACTAMENTE como están en el editor a un release nuevo del S3:\n\n' + present.join(', ') + '\n\nNo se regenera nada desde templates.',
      confirmText: 'Republicar',
      cancelText: 'Cancelar',
      tone: 'warning',
    })
    if (!ok) return
    setPublishFlow({ loading: true, step: 'publish-files', data: null, error: '' })
    try {
      const rawAssignment = reviewAssignment(item)
      const assignment = reviewPublishAssignment(item)
      console.log('[review republish] assignment', {
        source: reviewSource(item),
        assignmentType: assignment.assignmentType || '',
        username: rawAssignment.username || '',
        parentPcc: rawAssignment.parentPcc || '',
        pcc: rawAssignment.pcc || '',
      })
      const dataJsonTargetEnv = parseMaybeJSON(filesMap['data.json'], {})?.targetEnv
      const publishTargetEnv = resolveReviewPublishTargetEnv(window.location.href, dataJsonTargetEnv)
      const payload = {
        targetEnv: publishTargetEnv,
        source: 'review_dev',
        reviewId: item.id,
        id: item.id,
        scriptUuid: reviewScriptUuid,
        script_uuid: reviewScriptUuid,
        estado: item.estado,
        assignmentType: assignment.assignmentType || null,
        preferredLanguage: assignment.preferredLanguage || 'es',
        username: assignment.username || null,
        parentPcc: assignment.parentPcc || null,
        parent_pcc: assignment.parentPcc || null,
        pcc: assignment.pcc || null,
        updatedUser: 'admin_v2',
        script: {
          uuid: reviewScriptUuid,
          name: '',
          title: String(item.title || ''),
          targetEnv: publishTargetEnv,
        },
        files: present.map((path) => ({ path, content: String(filesMap[path]) })),
      }
      const validation = await biaValidateCustomScriptPublish(payload)
      if (validation?.ok === false) throw new Error(validation.message || 'No se pudo validar el paquete.')
      const publishResponse = await biaPublishReleaseCustomScript(payload)
      if (publishResponse?.ok === false) throw new Error(publishResponse.message || 'No se pudo republicar en S3.')
      item.history = appendHistory(item.history, { ts: new Date().toISOString(), action: 'republicó ' + present.length + ' archivo(s) tal cual (release ' + (publishResponse.releaseId || '?') + ')' })
      devQueue.update(item.id, { estado: 'publicado', history: item.history })
      setPublishFlow({ loading: false, step: '', data: publishResponse, error: '' })
      emit()
      toast('Republicado. URL: ' + (publishResponse.url || '(sin url)'), 'success', { title: 'Release nuevo con tus modificaciones' })
    } catch (e) {
      setPublishFlow({ loading: false, step: '', data: null, error: e.message })
      toast('No se pudo republicar: ' + e.message, 'danger')
    }
  }

  // Claves de la anatomía completa de un Custom Script (ver vista Anatomía).
  const ANATOMY_KEYS = ['index.html', 'app.js', 'api.js', 'styles.css', 'config_app.json', 'data.json', 'catalogs.json']

  function runtimeApiNeedsRefresh(content) {
    const text = String(content || '')
    if (!text.trim()) return true
    return false
  }

  /* Completa en item.files los archivos de la anatomía que falten, generándolos
     desde las MISMAS plantillas del builder que usa la publicación:
     index.html personalizado (title/name/uuid), app.js/api.js/styles.css tal cual,
     y config_app.json con el uuid de la revisión. NUNCA pisa archivos existentes,
     así el dev recibe la carpeta completa aunque el script todavía no esté publicado. */
  async function ensureAnatomyFiles(item) {
    if (!item) return []
    item.files = item.files || {}
    const added = []
    let dj = {}
    try { dj = parseDataJson(item) || {} } catch (_) { dj = {} }
    const title = String(dj.title || item.title || 'CUSTOM SCRIPT')
    const name = slugScriptName(title)
    const revUuid = item.scriptUuid || (item.published && item.published.uuid) || item._uuid || (item._uuid = uuidV4())

    if (item.files['index.html'] === undefined) {
      let tpl = await csFetchTemplate('index.html')
      tpl = tpl
        .replace(/<title>[^<]*<\/title>/i, '<title>' + title + '</title>')
        .replace(/id="headerTitle">[^<]*</, 'id="headerTitle">' + title + '<')
        .replace(/id="scriptNameBadge">[^<]*</, 'id="scriptNameBadge">' + name + '<')
        .replace(/id="uuidBadge">[^<]*</, 'id="uuidBadge">' + revUuid + '<')
      item.files['index.html'] = tpl
      added.push('index.html')
    }
    for (const tplName of ['app.js', 'api.js', 'styles.css']) {
      if (item.files[tplName] === undefined) {
        item.files[tplName] = tplName === 'app.js'
          ? `${await csFetchTemplate('sst-interpreter-v1.js')}\n${await csFetchTemplate('app.js')}`
          : await csFetchTemplate(tplName)
        added.push(tplName)
      }
    }
    // config_app.json COMPLETO para que el script corra igual que en el dashboard
    // (enableRuntimeApi + publicApiBaseUrl + env); sin esto, callAPI devuelve pending
    // y en un servidor local no lee la reserva. Merge no destructivo si ya existe.
    let runtimeCfg = { config: {} }
    try { runtimeCfg = csBuildConfigJson({ enableRuntimeApi: true }) || { config: {} } } catch (_) {}
    const existingCfg = parseMaybeJSON(item.files['config_app.json'], null)
    const mergedCfg = {
      ...(runtimeCfg || {}),
      ...(existingCfg || {}),
      config: {
        ...((runtimeCfg && runtimeCfg.config) || {}),
        ...((existingCfg && existingCfg.config) || {}),
        uuid: (existingCfg && existingCfg.config && existingCfg.config.uuid) || revUuid,
        useInternalConfig: true,
        enableRuntimeApi: true,
      },
    }
    const mergedCfgText = JSON.stringify(mergedCfg, null, 4)
    if (item.files['config_app.json'] !== mergedCfgText) {
      item.files['config_app.json'] = mergedCfgText
      if (existingCfg === null || item.files['config_app.json'] === undefined) added.push('config_app.json')
      else added.push('config_app.json (runtime habilitado)')
    }
    if (added.length) {
      appendHistory(item, 'anatomía completada: ' + added.join(', '))
      devQueue.update(item.id, { files: item.files, history: item.history })
      emit()
    }
    return added
  }

  async function actCompleteAnatomy() {
    const item = devQueue.get(dr.sel)
    if (!item) return
    try {
      const added = await ensureAnatomyFiles(item)
      toast(added.length ? 'Anatomía completada: ' + added.join(', ') : 'La anatomía ya está completa (no se pisó nada).', added.length ? 'success' : 'info')
    } catch (e) {
      toast('No se pudo completar la anatomía: ' + (e.message || String(e)), 'danger')
    }
  }

  async function actDownloadSourceFile(sourceFile) {
    const item = devQueue.get(dr.sel)
    if (!item || !sourceFile?.id) return
    try {
      const downloaded = await biaDownloadSourceFile(sourceFile.id, item.id)
      downloadSourceResponse(downloaded, sourceFile.originalFilename)
    } catch (error) {
      toast('No se pudo descargar el original: ' + (error.message || String(error)), 'danger')
    }
  }

  async function actDownloadAll() {
    const item = devQueue.get(dr.sel)
    if (!item) return
    toast('Armando ZIP con todos los archivos del script...', 'info')
    try {
      await ensureAnatomyFiles(item) // ZIP siempre con la carpeta completa del script
      const { default: JSZip } = await import('jszip')
      const zip = new JSZip()
      const manifest = ['ARCHIVOS DEL SCRIPT — ' + String(item.title || '') + ' — ' + new Date().toISOString(), '']
      const scriptFolder = slugScriptName(String(item.title || 'script'))
      zip.file(scriptFolder + '/README_PRUEBAS_LOCALES.md', [
        '# Probar este script en un servidor local',
        '',
        '1. Serví esta carpeta: `npx serve .` o `python -m http.server 8080`',
        '2. Abrí: `http://localhost:8080/index.html?token=<TOKEN_SABRE>&env=stg`',
        '   - El token es el mismo `?token=...` con el que el dashboard abre el script (copialo de la URL del iframe).',
        '   - `config_app.json` ya trae `enableRuntimeApi: true` + `publicApiBaseUrl`; sin token el runtime muestra \'No se recibió token\'.',
        '3. Con eso `callAPI` pega al mismo backend Boston que el dashboard (leer reserva, remarks, comandos).',
        '4. Si el navegador bloquea por CORS desde localhost, probar con el dominio permitido o pedir habilitar localhost en STG.',
        '',
        'Los archivos de esta carpeta son EXACTAMENTE los que se publican (mismas plantillas del builder).',
      ].join('\n'))
      manifest.push(scriptFolder + '/README_PRUEBAS_LOCALES.md')
      Object.entries(item.files || {}).forEach(([name, content]) => {
        // anatomía en la carpeta del script (como queda publicada); extras (feedback, notas) en revision/
        const dest = ANATOMY_KEYS.includes(name) || name === 'logic.json' || name === 'FICHA.md' ? scriptFolder + '/' + name : 'revision/' + name
        zip.file(dest, content == null ? '' : String(content))
        manifest.push(dest)
      })
      for (const sourceFile of item.sourceFiles || []) {
        try {
          const downloaded = await biaDownloadSourceFile(sourceFile.id, item.id) // eslint-disable-line no-await-in-loop
          zip.file('originales/' + downloaded.originalFilename, downloaded.contentBase64, { base64: true })
          manifest.push('originales/' + downloaded.originalFilename + ' — SHA-256 ' + downloaded.sha256)
        } catch (sourceError) {
          manifest.push('originales/' + (sourceFile.originalFilename || sourceFile.id) + ' — error: ' + sourceError.message)
        }
      }
      // URL publicada del script (para bajar la carpeta real de S3)
      let url = ''
      const uuid = item.published && item.published.uuid ? String(item.published.uuid).trim() : ''
      if (uuid) {
        try {
          const catalog = await scriptsCatalog()
          const rows = Array.isArray(catalog) ? catalog : (catalog && (catalog.scripts || catalog.customScripts || catalog.data)) || []
          const found = rows.find((s) => String(s.uuid || '').trim() === uuid)
          url = String((found && found.url) || '').trim()
        } catch (e) {
          manifest.push('(no se pudo resolver la URL del script publicado: ' + e.message + ')')
        }
      }
      if (url) {
        const base = url.split('?')[0].replace(/\/[^/]*$/, '')
        const grabbed = new Set()
        // Baja un archivo de la carpeta S3 del script y devuelve su texto (o null si falla).
        const fetchS3 = async (name) => {
          const rel = String(name).replace(/^\.?\//, '').split('?')[0].split('#')[0]
          if (!rel || grabbed.has(rel) || rel.includes('..')) return null
          grabbed.add(rel)
          try {
            const r = await fetch(base + '/' + rel, { cache: 'no-store' })
            if (r.ok) {
              const text = await r.text()
              zip.file('s3/' + rel, text)
              manifest.push('s3/' + rel)
              return text
            }
            manifest.push('s3/' + rel + ' — HTTP ' + r.status)
          } catch (e) {
            manifest.push('s3/' + rel + ' — error: ' + e.message)
          }
          return null
        }
        // 1) index.html primero: de ahí sacamos TODOS los assets realmente referenciados.
        const indexHtml = await fetchS3('index.html')
        if (indexHtml) {
          const refs = new Set()
          const rx = /(?:src|href)\s*=\s*["']([^"']+)["']/gi
          let m
          while ((m = rx.exec(indexHtml)) !== null) {
            const ref = m[1].trim()
            // Solo assets de la misma carpeta del script (relativos), no CDNs ni absolutos.
            if (ref && !/^(https?:)?\/\//i.test(ref) && !ref.startsWith('data:') && !ref.startsWith('#') && !ref.startsWith('/')) {
              refs.add(ref)
            }
          }
          for (const ref of refs) {
            await fetchS3(ref) // eslint-disable-line no-await-in-loop
          }
        }
        // 2) archivos conocidos del runtime, por si no están linkeados en el HTML.
        const S3_FILES = ['app.js', 'api.js', 'styles.css', 'data.json', 'catalogs.json', 'logic.json', 'config_app.json', 'FICHA.md']
        for (const f of S3_FILES) {
          await fetchS3(f) // eslint-disable-line no-await-in-loop
        }
        manifest.push('', 'Carpeta S3: ' + base)
      } else {
        manifest.push('', '(script sin URL publicada: el ZIP lleva solo los archivos de la revisión)')
      }
      zip.file('manifest.txt', manifest.join('\n'))
      const blob2 = await zip.generateAsync({ type: 'blob' })
      const a2 = document.createElement('a')
      a2.href = URL.createObjectURL(blob2)
      a2.download = 'script_' + String(item.title || 'revision').toLowerCase().replace(/[^a-z0-9-_]+/g, '_') + '_completo.zip'
      a2.click()
      URL.revokeObjectURL(a2.href)
      toast('ZIP generado con todos los archivos.', 'success')
    } catch (e) {
      toast('No se pudo armar el ZIP: ' + e.message, 'danger')
    }
  }

  async function actDone() {
    const item = devQueue.get(dr.sel)
    if (!item) return
    const feedbackFinal = reviewFeedback(item)
    if (!feedbackFinal) {
      toast('feedback inválido', 'danger')
      return
    }
    const nextHistory = appendHistory(item.history, { ts: new Date().toISOString(), action: 'marcado corregido ✓' })
    const persisted = await devQueueSetState(item.id, 'aprobado', feedbackFinal)
    if (persisted && persisted.ok === false) {
      toast(persisted.warn || 'No se pudo guardar el estado corregido en Revisión Dev.', 'danger')
      return
    }
    item.history = nextHistory
    item.estado = 'aprobado'
    item.feedback = feedbackFinal
    devQueue.updateLocal(item.id, { estado: 'aprobado', feedback: feedbackFinal, history: nextHistory })
    // LECCIÓN (pendiente): qué falló (feedback del envío) y qué aplicó el dev (diff original→corregido)
    try {
      const d = devDiffData(item.original || '{}', (item.files || {})['data.json'] || '{}')
      const editados = normalizeHistory(item.history)
        .filter((h) => /edit/.test(h.action || ''))
        .map((h) => h.action.replace('editó ', ''))
        .join(',')
      lecQueue.add({
        origen: 'dev',
        script: String(item.title || ''),
        instruccion: String(feedbackFinal || 'corrección del dev').slice(0, 300),
        resolucion: (lecResumirDiff(d) || 'editó: ' + (editados || 'archivos')).slice(0, 400),
      })
      // Mejora 5 — cerrar el loop: la lección viaja a la DB como conocimiento
      // INACTIVO (status 0). El admin la ve en Base de Conocimiento (filtro
      // "inactivo") y la ACTIVA desde cualquier máquina. Best-effort: si la API
      // falla, la lección local de arriba no se pierde.
      const resolucionTxt = (lecResumirDiff(d) || 'editó: ' + (editados || 'archivos')).slice(0, 400)
      biaCreateKnowledge({
        uuid: uuidV4(),
        title: ('Lección: ' + String(item.title || 'script')).slice(0, 100),
        category: 'lecciones',
        content: [
          'SCRIPT: ' + String(item.title || ''),
          '',
          'QUÉ FALLÓ (feedback del envío a revisión):',
          String(feedbackFinal || 'corrección del dev').slice(0, 600),
          '',
          'QUÉ APLICÓ EL DEV (diff original → corregido):',
          resolucionTxt,
        ].join('\n'),
        tags: { kind: 'leccion', origen: 'dev_review', script: String(item.title || ''), agents: ['agent1_sst', 'agent3_gen'], priority: 'normal' },
        status: 0,
        createdUser: 'dev_review',
        updatedUser: 'dev_review',
      }).then(() => {
        toast('Lección propuesta en la Base de Conocimiento (inactiva, pendiente de activación).', 'info')
      }).catch((err) => {
        console.warn('[leccion dev] no se pudo proponer en la KB:', err)
      })
    } catch (e) {
      console.warn('[leccion dev]', e)
    }
    emit()
  }

  async function actSendClientMessage() {
    const item = devQueue.get(dr.sel)
    if (!item) return
    const clean = String(clientMessage || '').trim()
    if (!clean) {
      toast('Escribe un mensaje para el cliente.', 'warning')
      return
    }
    setClientMessageBusy(true)
    try {
      const saved = await devQueueSendMessage(item.id, clean)
      const nextMessages = Array.isArray(item.messages) ? item.messages.slice() : []
      nextMessages.push(saved || {
        id: 'msg_' + Date.now().toString(36),
        reviewId: item.id,
        senderType: 'dev',
        senderUsername: 'dev_review',
        message: clean,
        readByClient: false,
        readByDev: true,
        createdDate: new Date().toISOString(),
      })
      devQueue.updateLocal(item.id, { messages: nextMessages })
      setClientMessage('')
      toast('Mensaje enviado al cliente.', 'success')
      notifyDevReviewQueueChanged()
      emit()
    } catch (e) {
      toast('No se pudo enviar el mensaje: ' + (e && e.message ? e.message : e), 'danger')
    } finally {
      setClientMessageBusy(false)
    }
  }

  async function actMarkDevMessagesRead() {
    const item = devQueue.get(dr.sel)
    if (!item) return
    try {
      await devQueueMarkMessagesRead(item.id)
      const nextMessages = (item.messages || []).map((message) => ({ ...message, readByDev: true }))
      devQueue.updateLocal(item.id, { messages: nextMessages })
      emit()
    } catch (e) {
      toast('No se pudieron marcar mensajes como leidos: ' + (e && e.message ? e.message : e), 'warning')
    }
  }

  async function actRemove() {
    const item = devQueue.get(dr.sel)
    if (!item) return
    const pubUuid = String((item.published && item.published.uuid) || item.scriptUuid || '').trim()
    const ok = await confirm({
      title: 'Eliminar definitivamente',
      body: `Se elimina "${item.title || ''}" de la cola de revisión EN EL SERVIDOR (no reaparece al recargar)${pubUuid ? ' y se intentará eliminar también el script publicado del repositorio' : ''}. Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
    })
    if (!ok) return
    // 1) Borrado PERSISTENTE del ítem de revisión (antes solo se borraba el
    //    localStorage y la sincronización con el server lo re-traía al recargar).
    try {
      await devQueueDelete(item.id)
    } catch (e) {
      // Ítems locales que nunca llegaron al server no existen allá: seguir igual.
      console.warn('[devQueue] delete server:', e && e.message)
    }
    // 2) Si tiene script publicado, best-effort de baja del repositorio
    //    (requiere token Sabre de la Consola GDS + config de API pública).
    if (pubUuid) {
      const cfg = (state.config || {})
      const base = String(cfg.publicApiBaseUrl || cfg.apiBaseUrl || '').replace(/\/+$/, '')
      if (base && state.sessionToken) {
        try {
          const rsp = await fetch(base + '/admin/ai?resource=dashboard&action=delete-script', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', token: state.sessionToken, 'x-api-key': cfg.apiKey || '' },
            body: JSON.stringify({ uuid: pubUuid }),
          })
          if (rsp.ok) toast('Script publicado eliminado del repositorio.', 'success')
          else toast('La revisión se eliminó, pero el script publicado no pudo borrarse (HTTP ' + rsp.status + '). Eliminalo desde el dashboard.', 'warning')
        } catch (e) {
          toast('La revisión se eliminó, pero el script publicado no pudo borrarse: ' + (e.message || e) + '. Eliminalo desde el dashboard.', 'warning')
        }
      } else {
        toast('La revisión se eliminó. El script publicado sigue en el repositorio (falta token Sabre o config de API): eliminalo desde el dashboard.', 'info')
      }
    }
    devQueue.remove(item.id)
    dr.sel = null
    toast('Revisión eliminada definitivamente.', 'success')
    emit()
  }

  /* ===== render ===== */

  const diff = it ? devDiffData(it.original || '', (it.files || {})['data.json'] || '') : null
  const noDiff = diff && !diff.added.length && !diff.removed.length && !diff.changed.length && !diff.topChanged.length
  const selectedOrigin = it ? reviewOriginDetails(it) : null
  const selectedOriginRows = selectedOrigin ? reviewOriginRows(selectedOrigin) : []
  const selectedDataJson = it
    ? parseMaybeJSON(cur === 'data.json' ? editorValue : it.files?.['data.json'], {}) || {}
    : {}
  const selectedIsSst = String(selectedDataJson?.executionMode || '').toLowerCase().trim() === 'sst_interpreter_v1'

  return (
    <>
      <div className="page-head">
        <div>
          <h1>🚨 Revisión Dev</h1>
          <p>
            Scripts que no cumplieron y fueron enviados con feedback. El dev{' '}
            <strong>edita los archivos, prueba, exporta/importa y marca corregido</strong>. Hoy la cola vive en localStorage; al subir al
            server, <code>devQueue</code> pasa a la DB (igual que keys/URLs y prompts).
          </p>
        </div>
        <div>
          <CButton color="primary" className="me-2" disabled={queueSyncBusy} onClick={refreshDevQueue}>
            {queueSyncBusy ? 'Actualizando…' : 'Actualizar cola'}
          </CButton>
          <CButton color="light" title='Subí un paquete de revisión editado (revision_*.json)' onClick={() => importRef.current && importRef.current.click()}>
            ⬆ Importar paquete
          </CButton>
          <input type="file" accept=".json" hidden ref={importRef} onChange={onImportFile} />
        </div>
      </div>

      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
        Ambiente: <strong>{reviewEnvironment.label}</strong> · API: <code>{reviewEnvironment.baseURL}</code> · Fuente: <strong>{queueVia === 'db' ? 'Base de datos' : 'Caché local'}</strong>
      </div>
      {queueSyncError ? <CAlert color="danger">{queueSyncError}</CAlert> : null}
      {queueCacheSummaryOnly ? (
        <CAlert color="info">
          La revisión es grande: sus archivos completos se mantienen en memoria desde la BD y la caché local guarda sólo el resumen. No se truncó el SST.
        </CAlert>
      ) : queueCacheTruncated ? (
        <CAlert color="warning">La caché local conserva sólo las revisiones más recientes; la lista visible proviene completa de la BD.</CAlert>
      ) : null}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <CButton color={activePanel === 'reviews' ? 'primary' : 'light'} onClick={() => setActivePanel('reviews')}>Revisiones</CButton>
        <CButton color={activePanel === 'sources' ? 'primary' : 'light'} onClick={() => setActivePanel('sources')}>Archivos originales</CButton>
      </div>

      {activePanel === 'sources' ? (
        <SourceFilesLibrary
          toast={toast}
          confirm={confirm}
          onDeleted={refreshDevQueue}
          onOpenReview={(reviewId) => {
            const review = list.find((entry) => entry.id === reviewId)
            if (review) {
              setReviewStatusFilter(reviewStatusBucket(review.estado))
              setReviewPage(1)
              selectItem(reviewId)
              setActivePanel('reviews')
            } else {
              toast('La revisión no está en la cola cargada. Actualiza la cola e inténtalo nuevamente.', 'warning')
            }
          }}
        />
      ) : (
      <>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(120px, 1fr))', gap: 8, marginBottom: 12 }}>
        <CButton
          color={reviewStatusFilter === 'review' ? 'warning' : 'light'}
          size="sm"
          onClick={() => {
            setReviewStatusFilter('review')
            setReviewPage(1)
            dr.sel = null
            dr.file = 'data.json'
            emit()
          }}
          title="Mostrar scripts en revision"
        >
          Revision <span className="bia-review-count">{reviewCount}</span>
        </CButton>
        <CButton
          color={reviewStatusFilter === 'corrected' ? 'success' : 'light'}
          size="sm"
          onClick={() => {
            setReviewStatusFilter('corrected')
            setReviewPage(1)
            dr.sel = null
            dr.file = 'data.json'
            emit()
          }}
          title="Mostrar scripts marcados como corregidos"
        >
          Corregido <span className="bia-review-count">{correctedCount}</span>
        </CButton>
        <CButton
          color={reviewStatusFilter === 'published' ? 'info' : 'light'}
          size="sm"
          onClick={() => {
            setReviewStatusFilter('published')
            setReviewPage(1)
            dr.sel = null
            dr.file = 'data.json'
            emit()
          }}
          title="Mostrar scripts completados"
        >
          Completado
        </CButton>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '340px minmax(0, 1fr)', gap: 14, alignItems: 'start' }}>
        {/* Cola de revisión */}
        <div>
          {filteredList.length ? (
            <>
              {pagedReviewList.map((x) => {
                const origin = reviewOriginDetails(x)
                const originRows = reviewOriginRows(origin)
                return (
                <div
                  key={x.id}
                  className="card-x"
                  style={{ cursor: 'pointer', marginBottom: 10, ...(dr.sel === x.id ? { border: '2px solid #f59e0b' } : {}) }}
                  onClick={() => selectItem(x.id)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <strong style={{ fontSize: 13 }}>{x.title || '(sin título)'}</strong>
                    {estadoBadge(x.estado || 'en_revision')}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                    {fmt(x.ts)}
                    {x.published ? (
                      <>
                        {' · pub: '}
                        <code>{String(x.published.uuid || x.published.name || '').slice(0, 12)}</code>
                      </>
                    ) : null}
                  </div>
                  {originRows.length ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 7 }}>
                      {originRows.map(([label, value]) => (
                        <span
                          key={label}
                          title={`${label}: ${value}`}
                          style={{
                            maxWidth: '100%',
                            fontSize: 10,
                            lineHeight: 1.2,
                            color: 'var(--muted)',
                            background: 'var(--bg)',
                            border: '1px solid var(--border)',
                            borderRadius: 4,
                            padding: '3px 5px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <b>{label}:</b> {value}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <div style={{ fontSize: 12, marginTop: 6, background: 'var(--bg)', padding: '6px 8px', borderRadius: 4 }}>
                    💬 {String(reviewFeedback(x) || 'Sin motivo registrado').slice(0, 140)}
                  </div>
                </div>
                )
              })}
              {reviewTotalPages > 1 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 4 }}>
                  <CButton
                    size="sm"
                    color="light"
                    disabled={reviewCurrentPage <= 1}
                    onClick={() => setReviewPage((page) => Math.max(1, page - 1))}
                  >
                    Anterior
                  </CButton>
                  <small className="text-muted">
                    Pagina {reviewCurrentPage} de {reviewTotalPages} - {filteredList.length} tarjetas
                  </small>
                  <CButton
                    size="sm"
                    color="light"
                    disabled={reviewCurrentPage >= reviewTotalPages}
                    onClick={() => setReviewPage((page) => Math.min(reviewTotalPages, page + 1))}
                  >
                    Siguiente
                  </CButton>
                </div>
              ) : null}
            </>
          ) : (
            <p className="bia-empty">
              No hay scripts en {reviewStatusFilter === 'corrected' ? 'corregido' : reviewStatusFilter === 'published' ? 'completado' : 'revision'}.
              <br />
              <small>
                {reviewStatusFilter === 'published'
                  ? 'Los scripts aparecen aqui despues de publicarse.'
                  : reviewStatusFilter === 'corrected'
                    ? 'Los scripts aparecen aqui despues de usar "Marcar corregido".'
                    : 'Se cargan con el boton "Enviar a revision (dev)" tras generar/publicar.'}
              </small>
            </p>
          )}
        </div>

        {/* Detalle / editor de archivos */}
        <div>
          {!it ? (
            <p className="bia-empty" style={{ marginTop: 8 }}>
              Elegí un script de la cola para editar sus archivos.
            </p>
          ) : (
            <div className="card-x">
              <div className="bia-card-head">
                <h3>✏ {it.title || ''}</h3>
                {estadoBadge(it.estado || 'en_revision')}
              </div>

              {selectedOriginRows.length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                  {selectedOriginRows.map(([label, value]) => (
                    <span
                      key={label}
                      title={`${label}: ${value}`}
                      style={{
                        fontSize: 11,
                        lineHeight: 1.2,
                        color: 'var(--muted)',
                        background: 'var(--bg)',
                        border: '1px solid var(--border)',
                        borderRadius: 4,
                        padding: '5px 7px',
                      }}
                    >
                      <b>{label}:</b> {value}
                    </span>
                  ))}
                </div>
              ) : null}

              {reviewFeedback(it) ? (
                <CAlert color="info" style={{ marginTop: 10, marginBottom: 10 }}>
                  <strong>Motivo de envío a revisión:</strong> {reviewFeedback(it)}
                </CAlert>
              ) : null}

              {(it.sourceFiles || []).length ? (
                <details className="card-x" style={{ marginTop: 10, marginBottom: 10 }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 700, userSelect: 'none' }}>
                    Archivos originales del cliente ({(it.sourceFiles || []).length})
                  </summary>
                  <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
                    {(it.sourceFiles || []).map((sourceFile) => (
                      <div key={sourceFile.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, border: '1px solid var(--border)', borderRadius: 6, padding: 8 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' }}>
                            <strong>{sourceFile.originalFilename}</strong>
                            <CBadge color={sourceFile.fileRole === 'catalog' ? 'info' : 'secondary'}>{sourceFile.fileRole === 'catalog' ? 'Catálogo' : 'Fuente'}</CBadge>
                            {sourceStatusBadge(sourceFile.linkStatus || (sourceFile.reviewId ? 'review' : sourceFile.scriptUuid ? 'published' : 'pending'))}
                          </div>
                          <div className="text-muted" style={{ fontSize: 11 }}>
                            {Math.max(1, Math.round(Number(sourceFile.sizeBytes || 0) / 1024))} KB · SHA-256 {String(sourceFile.sha256 || '').slice(0, 16)}…
                          </div>
                          <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
                            {sourceFile.parentPcc || it.parentPcc || 'PPCC desconocido'} / {sourceFile.pcc || it.pcc || 'PCC desconocido'}
                            {' · '}{sourceFile.username || sourceFile.createdUser || it.username || 'usuario desconocido'}
                            {sourceFile.createdDate ? ` · ${fmt(sourceFile.createdDate)}` : ''}
                          </div>
                          {sourceFile.importId ? <div className="text-muted" style={{ fontSize: 10 }}>Importación: <code>{sourceFile.importId}</code></div> : null}
                        </div>
                        <CButton size="sm" color="info" variant="outline" onClick={() => actDownloadSourceFile(sourceFile)}>
                          Descargar original
                        </CButton>
                      </div>
                    ))}
                  </div>
                </details>
              ) : null}

              <div className="card-x" style={{ marginTop: 10, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                  <h3 style={{ margin: 0 }}>Mensajes con el cliente</h3>
                  {(it.messages || []).some((message) => String(message.senderType || '').toLowerCase() === 'client' && !message.readByDev) ? (
                    <CButton size="sm" color="light" onClick={actMarkDevMessagesRead}>Marcar leído</CButton>
                  ) : null}
                </div>
                {(it.messages || []).length ? (
                  <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
                    {(it.messages || []).map((message) => {
                      const fromDev = String(message.senderType || '').toLowerCase() === 'dev'
                      const date = message.createdDate ? new Date(message.createdDate) : null
                      const dateText = date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : ''
                      return (
                        <div
                          key={message.id || `${message.senderType}-${message.createdDate}`}
                          style={{
                            border: '1px solid var(--border)',
                            borderRadius: 6,
                            padding: 8,
                            background: fromDev ? '#f8fafc' : '#fff7ed',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, color: 'var(--muted)' }}>
                            <strong>{fromDev ? 'Dev' : 'Cliente'}</strong>
                            <span>{dateText}</span>
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{message.message}</div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p className="text-muted" style={{ marginTop: 8, marginBottom: 0 }}>Sin mensajes todavía.</p>
                )}
                <CFormTextarea
                  style={{ marginTop: 10, minHeight: 82 }}
                  value={clientMessage}
                  onChange={(event) => setClientMessage(event.target.value)}
                  placeholder="Escribe una pregunta o aclaración para el cliente..."
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  <CButton color="info" disabled={clientMessageBusy || !clientMessage.trim()} onClick={actSendClientMessage}>
                    Enviar mensaje al cliente
                  </CButton>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '10px 0' }}>
                {files.map((f) => (
                  <CButton
                    key={f}
                    size="sm"
                    color={f === cur ? 'success' : 'light'}
                    className={f === cur ? 'btn-brand' : ''}
                    onClick={() => switchFile(f)}
                  >
                    📄 {f}
                  </CButton>
                ))}
              </div>

              <CFormTextarea
                id="dev-file-editor"
                className="bia-mono"
                style={{ width: '100%', minHeight: 320, lineHeight: 1.45 }}
                spellCheck={false}
                value={editorValue}
                onChange={(ev) => setEditorValue(ev.target.value)}
              />

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                <CButton color="success" className="btn-brand" onClick={actSave}>
                  💾 Guardar archivo
                </CButton>
                {selectedIsSst ? (
                  <>
                    {mockModeEnabled ? (
                      <CButton color="warning" onClick={actSstSimulation}>
                        ▶ Ejecución simulada SST
                      </CButton>
                    ) : null}
                    <CButton color="danger" style={{ color: '#fff' }} onClick={() => actPreview({ realPreview: true })}>
                      ⚠ Probar SST real en STG
                    </CButton>
                  </>
                ) : null}
                {!selectedIsSst ? (
                  <CButton color="light" onClick={() => actPreview({ realPreview: true })}>
                    🔍 Probar (vista previa)
                  </CButton>
                ) : null}
                {mockModeEnabled ? (
                  <CButton color="secondary" variant="outline" onClick={() => actPreview({ realPreview: false })}>
                    Probar seguro
                  </CButton>
                ) : null}
                <CButton color="light" onClick={actBuilder}>
                  ✏ Cargar al Builder
                </CButton>
                <CButton color="light" onClick={actExport}>
                  ⬇ Exportar paquete
                </CButton>
                <CButton
                  color="light"
                  style={{ borderColor: 'var(--brand)', color: 'var(--brand-dark)' }}
                  title="ZIP con los archivos de la revisión + TODOS los archivos publicados en S3 (index.html, app.js, api.js, styles.css, data.json, catalogs.json, logic.json, config_app.json)"
                  onClick={actDownloadAll}
                >
                  ⬇ Descargar TODO (ZIP)
                </CButton>
                <CButton
                  size="sm"
                  color="success"
                  variant="outline"
                  title="Genera los archivos que falten de la anatomía (index.html, app.js, api.js, styles.css, config_app.json) desde las plantillas del builder, sin pisar lo existente. Ideal cuando la revisión llegó solo con data.json + feedback."
                  onClick={actCompleteAnatomy}
                >
                  🧩 Completar anatomía
                </CButton>
                <CButton
                  color="light"
                  style={{ borderColor: 'var(--brand)', color: 'var(--brand-dark)' }}
                  title="Trae TODOS los archivos publicados en S3 al editor (index.html, app.js, api.js, styles.css, data.json, catalogs.json, logic.json, config_app.json…) para modificarlos acá. Lo ya editado en la revisión no se pisa."
                  onClick={actLoadS3ToEditor}
                >
                  📥 Cargar S3 en editor
                </CButton>
                <CButton
                  color="light"
                  style={{ borderColor: 'var(--brand)', color: 'var(--brand-dark)' }}
                  disabled={publishFlow.loading}
                  title="Sube TODOS los archivos del editor TAL CUAL a un release nuevo del S3 (sin regenerar desde templates). Requiere ✓ Marcar corregido."
                  onClick={actRepublishAsIs}
                >
                  🚀 Republicar tal cual
                </CButton>
                <CButton color="light" style={{ borderColor: 'var(--brand)', color: 'var(--brand-dark)' }} onClick={actDone}>
                  ✓ Marcar corregido
                </CButton>
                <CButton color="secondary" variant="outline" disabled={publishFlow.loading} onClick={() => saveReviewToLibrary()}>
                  Guardar en Biblioteca
                </CButton>
                <CButton color="secondary" variant="outline" disabled={publishFlow.loading} onClick={validateReviewPublish}>
                  Validar publicacion
                </CButton>
                <CButton
                  color="warning"
                  disabled={publishFlow.loading || !isCorrectedReviewState(it && it.estado)}
                  title={!isCorrectedReviewState(it && it.estado) ? 'Marca el script como corregido antes de publicar.' : ''}
                  onClick={approveAndPublish}
                >
                  Publicar en ambiente actual
                </CButton>
                <CButton color="danger" variant="outline" onClick={actRemove}>
                  🗑 Eliminar
                </CButton>
              </div>

              {(publishFlow.loading || publishFlow.data || publishFlow.error || (it && it.published)) ? (
                <div className="card-x" style={{ marginTop: 12 }}>
                  <h3>Publicacion</h3>
                  {publishFlow.loading ? (
                    <p className="text-muted" style={{ marginBottom: 0 }}>
                      Ejecutando {publishFlow.step || 'flujo'}...
                    </p>
                  ) : null}
                  {publishFlow.error ? (
                    <CAlert color="danger" style={{ marginBottom: 8 }}>
                      {publishFlow.error}
                    </CAlert>
                  ) : null}
                  {publishFlow.data ? (
                    <div style={{ fontSize: 12, display: 'grid', gap: 4 }}>
                      {publishFlow.data.filesReceived !== undefined ? (
                        <div>
                          Archivos recibidos: <b>{publishFlow.data.filesReceived}</b>
                        </div>
                      ) : null}
                      {publishFlow.data.totalSizeBytes !== undefined ? (
                        <div>
                          Tamano total: <b>{publishFlow.data.totalSizeBytes}</b> bytes
                        </div>
                      ) : null}
                      {publishFlow.data.readyToPublish !== undefined ? (
                        <div>
                          Listo para publicar: <b>{publishFlow.data.readyToPublish ? 'si' : 'no'}</b>
                        </div>
                      ) : null}
                      {publishFlow.data.provider ? (
                        <div>
                          Provider: <code>{publishFlow.data.provider}</code>
                        </div>
                      ) : null}
                      {publishFlow.data.releaseId ? (
                        <div>
                          Release ID: <code>{publishFlow.data.releaseId}</code>
                        </div>
                      ) : null}
                      {publishFlow.data.url ? (
                        <div>
                          URL:{' '}
                          <a href={publishFlow.data.url} target="_blank" rel="noreferrer">
                            {publishFlow.data.url}
                          </a>
                        </div>
                      ) : null}
                      {publishFlow.data.releaseTag ? (
                        <div>
                          Release: <code>{publishFlow.data.releaseTag}</code>
                        </div>
                      ) : null}
                      {publishFlow.data.commitSha ? (
                        <div>
                          Commit: <code>{String(publishFlow.data.commitSha).slice(0, 12)}</code>
                        </div>
                      ) : null}
                      {publishFlow.data.releaseUrl ? (
                        <div>
                          Release:{' '}
                          <a href={publishFlow.data.releaseUrl} target="_blank" rel="noreferrer">
                            {publishFlow.data.releaseUrl}
                          </a>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {it && it.published ? (
                    <div style={{ fontSize: 12, marginTop: publishFlow.data ? 8 : 0 }}>
                      Biblioteca: <code>{it.published.uuid || 'sin uuid'}</code>
                      {it.published.url ? (
                        <>
                          {' · '}
                          <a href={it.published.url} target="_blank" rel="noreferrer">
                            URL publicada
                          </a>
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {diff ? (
                <div className="card-x" style={{ marginTop: 12 }}>
                  <h3>🔭 Visión de cambios del dev (vs. lo enviado)</h3>
                  <div style={{ fontSize: '12.5px' }}>
                    {noDiff ? <span style={{ color: 'var(--muted)' }}>Sin cambios todavía — el data.json está igual a como llegó.</span> : null}
                    {diff.topChanged.length ? (
                      <div>
                        <strong>Top-level:</strong> {diff.topChanged.join(', ')}
                      </div>
                    ) : null}
                    {diff.added.length ? (
                      <div style={{ color: '#059669', marginTop: 4 }}>
                        <strong>+ Campos agregados:</strong> {diff.added.join(', ')}
                      </div>
                    ) : null}
                    {diff.removed.length ? (
                      <div style={{ color: '#dc2626', marginTop: 4 }}>
                        <strong>− Campos quitados:</strong> {diff.removed.join(', ')}
                      </div>
                    ) : null}
                    {diff.changed.length ? (
                      <div style={{ marginTop: 4 }}>
                        <strong>± Campos modificados:</strong>
                        <ul style={{ margin: '4px 0 0 18px' }}>
                          {diff.changed.map((c) => (
                            <li key={c.tag}>
                              <code>{c.tag}</code> → {c.props.join(', ')}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : (
                <CAlert color="warning" style={{ marginTop: 12 }}>
                  ⚠ data.json inválido — corregí el JSON para ver la visión de cambios.
                </CAlert>
              )}

              <details style={{ marginTop: 10 }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--muted)' }}>🕓 Historial</summary>
                <ul style={{ margin: '6px 0 0 18px', fontSize: 12 }}>
                  {normalizeHistory(it.history).map((h, i) => (
                    <li key={i}>
                      {fmt(h.ts)} — {h.action || ''}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </div>
      </div>
      </>
      )}

      {/* Vista previa funcional single-file (port mínimo de showPreviewModal) */}
      <CModal visible={Boolean(preview)} onClose={closePreview} size="xl" alignment="center">
        <CModalHeader closeButton={false}>
          <CModalTitle style={{ width: '100%' }}>
            <div style={{ fontSize: 11, opacity: 0.75, letterSpacing: '.5px', textTransform: 'uppercase' }}>
              {preview?.sstSimulation
                ? 'Ejecución simulada SST · Sin IA'
                : preview?.realPreview
                ? 'Prueba real controlada · STG'
                : 'Vista previa funcional · Single-file'}
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, marginTop: 2 }}>{preview ? preview.title : ''}</div>
          </CModalTitle>
        </CModalHeader>
        <CModalBody style={{ padding: 0 }}>
          <div style={{
            background: preview?.realPreview ? '#fee2e2' : '#fef3c7',
            color: preview?.realPreview ? '#991b1b' : '#92400e',
            padding: '8px 20px',
            fontSize: 12,
            borderBottom: `1px solid ${preview?.realPreview ? '#fca5a5' : '#fcd34d'}`,
          }}>
            Atencion: <b>{preview?.sstSimulation ? 'Simulación determinista:' : preview && preview.realPreview ? 'Vista previa real:' : 'Preview mode:'}</b>{' '}
            {preview?.sstSimulation
              ? 'recorre menús, formularios, CALL, GOTO, IF y comandos desde logic.json; ninguna operación se envía a Sabre.'
              : preview && preview.realPreview
              ? 'usa el token Sabre y puede ejecutar comandos reales.'
              : 'las llamadas a Sabre estan simuladas. El form, validacion, postCommands y placeholders se ejecutan reales.'}
          </div>
          <div style={{ height: '60vh', position: 'relative', background: '#f8fafc' }}>
            {preview?.sstSimulation ? (
              <div style={{ position: 'absolute', inset: 0, overflow: 'auto' }}>
                <SstInterpreterPreview ir={preview.ir || {}} logicJson={preview.logicJson || {}} fileName={preview.title} />
              </div>
            ) : preview?.realPreview && !preview.realArmed ? (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                <div style={{ width: 'min(100%, 620px)', padding: 24, border: '1px solid #fca5a5', borderRadius: 12, background: '#fff', boxShadow: '0 12px 30px rgba(127,29,29,.12)' }}>
                  <div style={{ color: '#b91c1c', fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Prueba con comandos reales</div>
                  <p style={{ margin: '0 0 10px', color: '#374151' }}>
                    El paquete está listo, pero todavía no se ha iniciado. Al iniciar podrá enviar comandos a la sesión Sabre activa.
                  </p>
                  <ul style={{ margin: '0 0 16px', paddingLeft: 20, color: '#4b5563', fontSize: 13 }}>
                    <li>Ambiente forzado: <b>STG</b></li>
                    <li>Token Sabre: <b>{preview.tokenPresent ? 'presente' : 'ausente'}</b></li>
                    <li>Endpoint: <code>https://stg.boston.basset.ws</code></li>
                    <li>Utiliza exclusivamente una reserva de prueba.</li>
                  </ul>
                  <CButton color="danger" style={{ color: '#fff' }} disabled={!preview.url || !preview.tokenPresent} onClick={armRealPreview}>
                    Entiendo · Iniciar prueba real en STG
                  </CButton>
                </div>
              </div>
            ) : preview && preview.url ? (
              <iframe
                title="Vista previa del script"
                src={preview.url}
                sandbox="allow-scripts allow-forms allow-popups allow-modals allow-same-origin"
                style={{ width: '100%', height: '100%', border: 0, background: '#fff' }}
              />
            ) : preview && preview.error ? (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center', color: '#dc2626' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>⚠</div>
                  <div>
                    <b>Error armando el preview</b>
                  </div>
                  <div style={{ fontSize: 12, marginTop: 6, opacity: 0.85 }}>{preview.error}</div>
                  <div style={{ fontSize: 11, marginTop: 8, opacity: 0.6 }}>Revisá la consola para detalles.</div>
                </div>
              </div>
            ) : (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
                  <div>Empaquetando script en un único archivo…</div>
                </div>
              </div>
            )}
          </div>
        </CModalBody>
        <CModalFooter style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>
            <b>{(preview && preview.dataJson && (preview.dataJson.forms || []).length) || 0}</b> form(s) ·{' '}
            <b>{(preview && preview.dataJson && (preview.dataJson.postCommands || []).length) || 0}</b> postCommand(s) · UUID:{' '}
            <code>{(preview && preview.configJson && preview.configJson.config && preview.configJson.config.uuid) || '—'}</code>
          </span>
          <div className="bia-row-input">
            {!preview?.sstSimulation ? (
              <>
                <CButton color="light" disabled={!(preview && preview.url)} title="Descargar .html autocontenido" onClick={downloadPreviewHtml}>
                  ⬇ Descargar HTML único
                </CButton>
                <CButton
                  color="light"
                  disabled={!(preview && preview.url)}
                  title="Abrir en pestaña nueva"
                  onClick={() => window.open(preview.url, '_blank')}
                >
                  ↗ Pestaña nueva
                </CButton>
              </>
            ) : null}
            <CButton color="danger" style={{ color: '#fff' }} onClick={closePreview}>
              ✕ Cerrar
            </CButton>
          </div>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default DevReviewView
